import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Group, Stack, Text, Avatar, Badge, Button, Title, UnstyledButton,
  TextInput, ActionIcon, Tooltip, Table, Modal, Tabs,
  Paper, Select, Menu, Divider, Pagination, SimpleGrid,
  Card, RingProgress, Progress, Grid, LoadingOverlay, Center, Flex, Checkbox, Switch, PasswordInput,
  Drawer, ScrollArea, Indicator
} from '@mantine/core';


import {UserModal} from './modals/UserModal';
import {ClientModal} from './modals/ClientModal';
import {PermissionModal} from './modals/PermissionModal';
import {CallModal} from './modals/CallModal';
import {ChatModal} from './modals/ChatModal';
import {QueueModal} from './modals/QueueModal';
import { DashboardTabs } from './tabs/DashboardTabs';

// Import state management hooks
import { useModalManager } from '../hooks/useModalManager';
import {useTabState} from '../hooks/useTabState'
import { notifications } from '@mantine/notifications';
import VoucherDiagnosticSidebar from './VoucherDiagnosticSidebar';
import {
  IconSearch, IconPlus, IconEdit, IconTrash, IconDots,
  IconUser, IconUsers, IconShield, IconSettings, IconChartBar,
  IconLock, IconMail, IconPhone, IconBuilding, IconUserPlus,
  IconUserMinus, IconRefresh, IconChevronDown, IconChevronUp, IconCheck, IconX,
  IconMessage, IconClock, IconActivity, IconStar, IconTrendingUp, IconLogout,
  IconHeadset, IconArrowUp, IconArrowDown, IconMinus, IconBolt, IconTarget,
  IconChartLine, IconMoodSmile, IconPlayerPlay, IconCalendar, IconCircleCheck,
  IconWifi, IconDevices, IconTicket, IconDatabase, IconClockPlus,
  IconFingerprint, IconRotate, IconPlugConnected, IconAlertTriangle,
  IconCircleDot, IconCloudComputing, IconNetworkOff, IconInbox,
  IconSend, IconPaperclip, IconPhoto, IconMicrophone, IconCamera,
  IconPhoneOff, IconVideo
} from '@tabler/icons-react';
import classes from './SuperAdminDashboard.module.css';
import socketConnection from './services/socketConnection';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3004/api';
const socket = socketConnection();

/* ── Helpers ── */
const COLORS = ['violet', 'blue', 'pink', 'grape', 'orange', 'indigo', 'yellow', 'cyan'];
const avatarColor = (n) => { let h = 0; for (let c of (n || '')) h += c.charCodeAt(0); return COLORS[h % COLORS.length]; };
const getInitial = (n) => (n || '?').charAt(0).toUpperCase();

const toast = (msg, color = 'blue') =>
  notifications.show({ message: msg, color, autoClose: 2500 });

const formatTime = (ts) => { if (!ts) return ''; const d = new Date(ts); return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); };
const formatCallDuration = (seconds) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`; };
const EMOJIS = ['😊','😂','❤️','👍','🎉','🔥','👋','🙏','💯','✅','⭐','🚀'];

// Role-based access config
const ROLE_CONFIG = {
  super_admin: {
    label: 'Super Admin',
    panelTitle: 'Super Admin Panel',
    color: 'violet',
    gradient: { from: '#9381ff', to: '#b8b8ff' },
    storagePrefix: 'superadmin',
    tabs: ['dashboard', 'users', 'clients', 'transactions', 'captive_portal'],
    canCreateUser: true,
    canEditUser: true,
    canDeleteUser: true,
    canCreateClient: true,
    canEditClient: true,
    canDeleteClient: true,
    allowedRoles: ['super_admin'],
    // Super admin can see all roles
    visibleUserRoles: ['super_admin', 'manager', 'supervisor', 'helpdesk_agent'],
    roleFilterOptions: [
      { value: 'all', label: 'All Roles' },
      { value: 'helpdesk_agent', label: 'Helpdesk Agent' },
      { value: 'manager', label: 'Manager' },
      { value: 'supervisor', label: 'Supervisor' },
      { value: 'super_admin', label: 'Super Admin' },
    ],
  },
  manager: {
    label: 'Manager',
    panelTitle: 'Manager Panel',
    color: 'blue',
    gradient: { from: 'blue', to: 'cyan' },
    storagePrefix: 'manager',
    tabs: ['dashboard', 'users', 'transactions', 'captive_portal', 'queue'],
    canCreateUser: true,
    canEditUser: true,
    canDeleteUser: false,
    canCreateClient: false,
    canEditClient: false,
    canDeleteClient: false,
    allowedRoles: ['manager'],
    // Manager can only see helpdesk agents
    visibleUserRoles: ['helpdesk_agent'],
    roleFilterOptions: [
      { value: 'all', label: 'All' },
      { value: 'helpdesk_agent', label: 'Helpdesk Agent' },
    ],
  },
  supervisor: {
    label: 'Supervisor',
    panelTitle: 'Supervisor Panel',
    color: 'cyan',
    gradient: { from: 'cyan', to: '#9381ff' },
    storagePrefix: 'supervisor',
    tabs: ['dashboard', 'users', 'transactions', 'captive_portal', 'queue'],
    canCreateUser: true,
    canEditUser: true,
    canDeleteUser: false,
    canCreateClient: false,
    canEditClient: false,
    canDeleteClient: false,
    allowedRoles: ['supervisor'],
    // Supervisor can see managers and helpdesk agents
    visibleUserRoles: ['manager', 'helpdesk_agent'],
    roleFilterOptions: [
      { value: 'all', label: 'All' },
      { value: 'helpdesk_agent', label: 'Helpdesk Agent' },
      { value: 'manager', label: 'Manager' },
    ],
  },
};

export default function SuperAdminDashboard({ role: propRole = 'super_admin' }) {
  // ========== Role Config ==========
  const roleConfig = ROLE_CONFIG[propRole] || ROLE_CONFIG.super_admin;
  
  // ========== Navigation ==========
  const navigate = useNavigate();
  
  // ========== Tab State ==========
  const dashboardTabs = useTabState('dashboard');

  // ========== Modal States (Using new hook) ==========
  const modals = useModalManager({
    user: { mode: 'create', data: {}, errors: {} },
    client: { mode: 'create', data: {}, errors: {} },
    permission: { mode: 'create', data: {}, errors: {} },
    role: { mode: 'create', data: {}, errors: {} },
    call: { data: null },
    queue: { data: [] },
    chat: { data: null }
  });
  
  // Modal state accessors
  const isUserModalOpen = modals.isOpen('user');
  const isClientModalOpen = modals.isOpen('client');
  const isPermissionModalOpen = modals.isOpen('permission');
  const isRoleModalOpen = modals.isOpen('role');
  const isQueueModalOpen = modals.isOpen('queue');
  const isChatModalOpen = modals.isOpen('chat');
  const isCallModalOpen = modals.isOpen('call');
  
  // Get modal data
  const userModalState = modals.getModalState('user');
  const clientModalState = modals.getModalState('client');
  const permissionModalState = modals.getModalState('permission');
  const roleModalState = modals.getModalState('role');
  const queueModalState = modals.getModalState('queue');
  const chatModalState = modals.getModalState('chat');
  const callModalState = modals.getModalState('call');
  
  // Call type state (separate from modal - used for video/audio call type tracking)
  const [callType, setCallType] = useState(null);

  // Modal event handlers - adapt component interface to form state
  const handleUserFieldChange = (field, value) => {
    setUserForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleClientFieldChange = (field, value) => {
    setClientForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handlePermissionFieldChange = (field, value) => {
    setPermissionForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleRoleFieldChange = (field, value) => {
    setRoleForm(prev => ({ ...prev, [field]: value }));
  };
  
  // Queue state for transferred chats
  const [transferredChats, setTransferredChats] = useState([]); // Pending escalations
  const [acceptedChats, setAcceptedChats] = useState([]); // Chats manager is actively working on
  const [currentAcceptedChat, setCurrentAcceptedChat] = useState(null); // Currently viewing accepted chat
  const [escalationHistory, setEscalationHistory] = useState([]); // Completed escalation history
  const [showEscalationQueue, setShowEscalationQueue] = useState(true); // Collapsible queue
  const [escalationHistorySearch, setEscalationHistorySearch] = useState('');
  const [escalationNotesModal, setEscalationNotesModal] = useState(null); // { clientName, notes } for viewing summary
  const [selectedChat, setSelectedChat] = useState(null);
  const [escalatedActiveChats, setEscalatedActiveChats] = useState([]); // Escalated active chats from database

  // Deduplicated escalated chats — exclude any that already appear in transferredChats or acceptedChats
  const dedupedEscalatedChats = useMemo(() => {
    const transferredRoomIds = new Set(transferredChats.map(c => c.roomId));
    const acceptedRoomIds = new Set(acceptedChats.map(c => c.roomId));
    return escalatedActiveChats.filter(c => !transferredRoomIds.has(c.roomId) && !acceptedRoomIds.has(c.roomId));
  }, [escalatedActiveChats, transferredChats, acceptedChats]);

  const selectedChatRef = useRef(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatReply, setChatReply] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const chatMessagesEnd = useRef(null);

  // Call state
  const [callStatus, setCallStatus] = useState(null);
  const [callTargetRoomId, setCallTargetRoomId] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const peerConnection = useRef(null);
  const iceCandidateBuffer = useRef([]);
  const [stats, setStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Keep selectedChat ref in sync so socket listeners always have the current value
  useEffect(() => { selectedChatRef.current = selectedChat; }, [selectedChat]);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userForm, setUserForm] = useState({
    username: '', email: '', password: '', fullName: '', 
    role: 'helpdesk_agent', department: '', phone: '',
    status: 'active',
    permissions: {
      canManageUsers: false,
      canManageClients: false,
      canViewReports: false,
      canManageSettings: false,
      canHandleChats: true
    }
  });
  
  // Clients state
  const [clients, setClients] = useState([]);
  const [clientsPage, setClientsPage] = useState(1);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [clientForm, setClientForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', notes: ''
  });
  
  // Voucher Diagnostic Sidebar state
  const [voucherSidebarOpen, setVoucherSidebarOpen] = useState(false);
  const [voucherClientEmail, setVoucherClientEmail] = useState('');

  // Search & filters
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [clientSearch, setClientSearch] = useState('');

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionStatusFilter, setTransactionStatusFilter] = useState('all');
  const [transactionSearch, setTransactionSearch] = useState('');
  
  // Roles state - load from database API
  const [roles, setRoles] = useState([]);
  
  // Permissions state - load from database API
  const [permissions, setPermissions] = useState([]);
  
  const [permissionForm, setPermissionForm] = useState({ id: '', name: '', description: '', active: true, permissions: [] });

  // Role form state
  const [roleForm, setRoleForm] = useState({ id: '', name: '', description: '', color: 'blue', permissions: [] });

  // Active tab state for dashboard
  const [activeTab, setActiveTab] = useState('dashboard');

  // Loading state
  const [loading, setLoading] = useState(false);

  // Profile states
  const [profileOpen, setProfileOpen] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileForm, setEditProfileForm] = useState({ fullName: '', phone: '', department: '' });
  const [myStatus, setMyStatus] = useState('online'); // 'online' or 'away'

  // CaptselectedChatSession, setSelectedChatSession] = useState(null); // Currently selected chat for diagnostics
  const [voucherDiag, setVoucherDiag] = useState(null); // Diagnostic data for selected chat
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherActionLoading, setVoucherActionLoading] = useState(''); // 'reset-mac' | 'extend' | 'manual-auth' | ''
  const [manualAuthMac, setManualAuthMac] = useState(''); // MAC input for manual auth
  const [manualAuthDuration, setManualAuthDuration] = useState('60'); // Duration input for manual auth
  const [voucherDiagTimer, setVoucherDiagTimer] = useState(null); // Auto-refresh timer

  // Load user from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
    const user = localStorage.getItem(`${roleConfig.storagePrefix}User`);
    
    if (!token || !user) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(user);
    setCurrentUser(userData);
    
    // Only allow the correct role(s) to access this panel
    if (!roleConfig.allowedRoles.includes(userData.role)) {
      navigate('/login');
      return;
    }
    
    // Load active/accepted chats from database for manager/supervisor roles
    if (propRole === 'manager' || propRole === 'supervisor') {
      loadAcceptedChats();
      loadEscalatedActiveChats();
    }
  }, [navigate, roleConfig]);

  // Load accepted chats from database for persistence across page reloads
  const loadAcceptedChats = async () => {
    try {
      const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
      const response = await fetch(`${API_URL}/chat-sessions?status=active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const sessions = await response.json();
        // Filter sessions assigned to current user (by agentId or agentName) AND status is active
        const userData = JSON.parse(localStorage.getItem(`${roleConfig.storagePrefix}User`) || '{}');
        const userChats = sessions.filter(session => 
          (session.agentId === userData._id || session.agentName === userData.fullName) &&
          session.status === 'active'
        );
        
        if (userChats.length > 0) {
          // Load first accepted chat into chat modal
          const firstChat = userChats[0];
          setSelectedChat({
            roomId: firstChat.roomId,
            clientName: firstChat.userName,
            fromAgentName: firstChat.agentName,
            acceptedAt: firstChat.acceptedAt
          });
          setCurrentAcceptedChat({
            roomId: firstChat.roomId,
            clientName: firstChat.userName,
            fromAgentName: firstChat.agentName,
            acceptedAt: firstChat.acceptedAt
          });
          setAcceptedChats(prev => {
            // Use functional updater to access current state and avoid stale closure
            const newChats = userChats.filter(chat =>
              !prev.some(c => c.roomId === chat.roomId)
            );
            return [...prev, ...newChats.map(chat => ({
              roomId: chat.roomId,
              clientName: chat.userName,
              fromAgentName: chat.agentName,
              acceptedAt: chat.acceptedAt,
              status: 'accepted'
            }))];
          });
          setChatMessages(firstChat.messages || []);
          // Only open chat modal if manager is on the queue tab
          if (activeTab === 'queue') {
            modals.openModal('chat');
          }
          notifications.show({
            title: 'Restored Chat',
            message: `Resumed chat with ${firstChat.userName}`,
            color: 'blue',
            autoClose: 3000
          });
        }
      }
    } catch (error) {
      console.error('Error loading accepted chats:', error);
    }
  };

  // Load escalated active chats from database (escalated=true, resolved=false, status=active)
  // Exclude chats that are already accepted by a manager (have agentId set)
  // Also exclude chats that are already in transferredChats to avoid duplicates in pending queue
  const loadEscalatedActiveChats = async () => {
    try {
      const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
      const response = await fetch(`${API_URL}/chat-sessions?escalated=true&resolved=false&status=active`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const sessions = await response.json();
        // Filter out chats already accepted by this manager (agentId matches current user)
        // Keep chats that still have the original helpdesk agent's agentId — those are pending
        const userData = JSON.parse(localStorage.getItem(`${roleConfig.storagePrefix}User`) || '{}');
        const pendingEscalatedChats = sessions.filter(chat => chat.agentId !== userData._id);
        const mapped = pendingEscalatedChats.map(chat => ({
          roomId: chat.roomId,
          clientName: chat.userName,
          clientEmail: chat.email,
          fromAgentName: chat.agentName,
          escalatedAt: chat.escalatedAt,
          escalatedTo: chat.escalatedTo,
          notes: chat.notes,
          subject: chat.subject
        }));
        // Use functional updater to avoid stale closure — filter against current transferredChats
        setEscalatedActiveChats(mapped);
      }
    } catch (error) {
      console.error('Error loading escalated active chats:', error);
    }
  };


  useEffect(() => {
    if (transactions) {
      console.log('Transactions updated:', transactions);
    }
  }, [transactions]);

  // Real-time agent statuses from socket
  const [agentStatuses, setAgentStatuses] = useState([]);

  // Join superadmin socket room, register as agent, and listen for agent status updates
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem(`${roleConfig.storagePrefix}User`) || '{}');
    
    // Function to join rooms and emit events
    const joinRooms = () => {
      socket.emit('superadmin:join');
      
      // Join manager room for manager/supervisor roles to receive transfers
      if (propRole === 'manager' || propRole === 'supervisor') {
        socket.emit('manager:join', {
          userId: userData._id,
          username: userData.username,
          fullName: userData.fullName,
          role: propRole
        });
      }
      
      // Register this user as a connected agent so their status is tracked
      if (userData._id) {
        socket.emit('admin:join', {
          userId: userData._id,
          username: userData.username,
          fullName: userData.fullName,
          role: userData.role,
          status: 'active'
        });
        socket.emit('agent:status_change', { userId: userData._id, status: 'active' });
      }
    };
    
    // Check if socket is already connected, otherwise wait for connection
    if (socket.connected) {
      joinRooms();
    } else {
      socket.on('connect', () => {
        console.log('Socket connected, joining rooms...');
        joinRooms();
      });
    }
    
    socket.on('agents:status_update', (statuses) => {
      // Filter out offline entries for the agentStatuses state (used for indicator dots)
      // but keep them for processing user list updates
      setAgentStatuses(statuses.filter(a => a.status !== 'offline'));
      
      // Update the account status in users list in real-time
      setUsers(prevUsers => prevUsers.map(user => {
        const liveAgent = statuses.find(a => a.userId === user._id);
        if (liveAgent) {
          // Map socket status to DB-style status for display
          // 'offline' → 'inactive', everything else stays as-is
          const mappedStatus = liveAgent.status === 'offline' ? 'inactive' : liveAgent.status;
          return { ...user, status: mappedStatus };
        }
        return user;
      }));
    });

    // Listen for incoming transfers from helpdesk agents
    socket.on('manager:transfer', (data) => {
      const { roomId, clientName, clientEmail, fromAgentName, message, timestamp, notes, subject } = data;
      const newTransfer = {
        roomId,
        clientName: clientName || 'Unknown Client',
        clientEmail: clientEmail || '',
        fromAgentName: fromAgentName || 'Unknown Agent',
        message: message || 'Chat transferred to you',
        timestamp: timestamp || new Date().toISOString(),
        status: 'pending',
        notes: notes || '',
        subject: subject || '',
      };
      // Deduplicate — manager receives from both manager_room and superadmin_room
      setTransferredChats(prev => {
        if (prev.some(c => c.roomId === roomId)) return prev;
        return [...prev, newTransfer];
      });
      // Remove from escalatedActiveChats if it exists there (DB-fetched) to avoid duplicates
      setEscalatedActiveChats(prev => prev.filter(c => c.roomId !== roomId));
      notifications.show({
        title: 'New Chat Transfer',
        message: `${clientName} has been transferred to you`,
        color: 'orange',
        autoClose: 5000
      });
      // Refresh escalated active chats when a new transfer comes in
      loadEscalatedActiveChats();
    });

    // Listen for messages in the joined room (customer messages come through here)
    // Uses selectedChatRef to avoid stale closure — selectedChat would be null when this effect runs
    // Deduplicates by timestamp since manager receives from both the room and manager_room
    socket.on('user:message', (msg) => {
      const current = selectedChatRef.current;
      if (msg && msg.sender === 'user' && current?.roomId === msg.roomId) {
        setChatMessages(prev => {
          const isDup = prev.length > 0 && prev[prev.length - 1].timestamp === msg.timestamp && prev[prev.length - 1].text === msg.text;
          return isDup ? prev : [...prev, msg];
        });
      }
    });

    // Call socket listeners
    socket.on('call:incoming', ({ callType, from, callerSocketId }) => {
      setIncomingCall({ callType, from });
      setCallTargetRoomId(callerSocketId);
      setCallType(callType);
      notifications.show({ title: 'Incoming call', message: `${from} is calling you (${callType})`, color: 'green' });
    });
    socket.on('call:accepted', ({ acceptedBy }) => {
      setCallTargetRoomId(acceptedBy);
      setCallStartTime(Date.now());
      setCallStatus('connected');
      // Manager is the caller — create offer via helper (defined outside useEffect)
      createPeerConnectionAndOfferRef.current(acceptedBy);
    });
    socket.on('call:rejected', () => {
      setCallStatus(null); setCallType(null);
      cleanupCallRef.current();
      toast('Call was rejected', 'red');
    });
    socket.on('call:ended', ({ duration }) => {
      const durationSec = duration || 0;
      const durationStr = formatCallDuration(durationSec);
      if (durationSec > 0) {
        setChatMessages(prev => [...prev, { sender: 'system', text: `📞 Call ended - Duration: ${durationStr}`, timestamp: new Date().toISOString(), isCallEnded: true }]);
      }
      cleanupCallRef.current();
      setCallStatus(null); setCallType(null); setIncomingCall(null);
      setCallTargetRoomId(null); setCallDuration(0); setCallStartTime(null);
      toast(`Call ended - Duration: ${durationStr}`, 'blue');
    });
    socket.on('call:offer', async ({ offer, from }) => {
      await handleOfferRef.current(offer, from);
    });
    socket.on('call:answer', async ({ answer }) => {
      if (peerConnection.current && peerConnection.current.signalingState === 'have-local-offer') {
        await peerConnection.current.setRemoteDescription(new RTCSessionDescription(answer));
        while (iceCandidateBuffer.current.length > 0) {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(iceCandidateBuffer.current.shift()));
        }
      }
    });
    socket.on('call:ice-candidate', async ({ candidate }) => {
      if (peerConnection.current && peerConnection.current.remoteDescription) {
        try { await peerConnection.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch (e) { console.error('ICE error:', e); }
      } else {
        iceCandidateBuffer.current.push(candidate);
      }
    });

    return () => {
      socket.off('connect');
      socket.off('agents:status_update');
      socket.off('manager:transfer');
      socket.off('user:message');
      socket.off('call:incoming');
      socket.off('call:accepted');
      socket.off('call:rejected');
      socket.off('call:ended');
      socket.off('call:offer');
      socket.off('call:answer');
      socket.off('call:ice-candidate');
    };
  }, [roleConfig.storagePrefix]);

  // Call duration timer
  useEffect(() => {
    if (callStatus === 'connected' && callStartTime) {
      const interval = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - callStartTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callStatus, callStartTime]);

  // Auto-scroll chat messages
  useEffect(() => { chatMessagesEnd.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // ── WebRTC Call Functions ──
  const createPeerConnectionAndOfferRef = useRef(null);
  const handleOfferRef = useRef(null);
  const cleanupCallRef = useRef(null);

  const getTargetSocketId = () => callTargetRoomId;

  const createPeerConnection = (targetSocketId) => {
    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
      ]
    });
    pc.onicecandidate = (event) => {
      if (event.candidate) socket.emit('call:ice-candidate', { targetSocketId, candidate: event.candidate });
    };
    pc.ontrack = (event) => setRemoteStream(event.streams[0]);
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') toast('Call connection lost', 'red');
    };
    peerConnection.current = pc;
    return pc;
  };

  const getLocalMedia = async (isVideo) => {
    const stream = await navigator.mediaDevices.getUserMedia({ video: isVideo, audio: true });
    setLocalStream(stream);
    return stream;
  };

  const cleanupCall = () => {
    if (localStream) { localStream.getTracks().forEach(t => t.stop()); setLocalStream(null); }
    if (peerConnection.current) {
      peerConnection.current.getSenders().forEach(s => { if (s.track) s.track.stop(); });
      peerConnection.current.close(); peerConnection.current = null;
    }
    if (remoteStream) { remoteStream.getTracks().forEach(t => t.stop()); setRemoteStream(null); }
    iceCandidateBuffer.current = [];
  };

  // Called when manager is the CALLER (manager initiated, customer accepted)
  const createPeerConnectionAndOffer = async (targetId) => {
    try {
      const isVideo = callType === 'video';
      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnection(targetId);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('call:offer', { targetSocketId: targetId, offer });
    } catch (err) {
      console.error('Error creating offer:', err);
      toast('Could not access camera/microphone', 'red');
      cleanupCall();
      setCallStatus(null); setCallType(null);
    }
  };
  createPeerConnectionAndOfferRef.current = createPeerConnectionAndOffer;

  // Called when manager receives an offer (manager is the CALLEE)
  const handleOffer = async (offer, fromSocketId) => {
    try {
      const target = fromSocketId;
      if (!callTargetRoomId) setCallTargetRoomId(target);
      const isVideo = callType === 'video';
      const stream = await getLocalMedia(isVideo);
      const pc = createPeerConnection(target);
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      while (iceCandidateBuffer.current.length > 0) {
        await pc.addIceCandidate(new RTCIceCandidate(iceCandidateBuffer.current.shift()));
      }
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      socket.emit('call:answer', { targetSocketId: target, answer });
    } catch (err) {
      console.error('Error handling offer:', err);
    }
  };
  handleOfferRef.current = handleOffer;
  cleanupCallRef.current = cleanupCall;

  const initiateCall = (callType) => {
    const targetRoom = selectedChat?.roomId;
    if (!targetRoom) { toast('No chat selected', 'orange'); return; }
    setCallTargetRoomId(targetRoom);
    setCallType(callType);
    setCallStatus('calling');
    socket.emit('call:initiate', { roomId: targetRoom, callType });
    toast(`Calling ${selectedChat?.clientName || 'Client'}...`, 'blue');
  };

  const acceptCall = () => {
    const target = callTargetRoomId;
    socket.emit('call:accept', { targetSocketId: target });
    setCallStatus('connected');
    setCallStartTime(Date.now());
    setIncomingCall(null);
    // Caller creates offer — we wait for it
  };

  const rejectCall = () => {
    socket.emit('call:reject', { targetSocketId: callTargetRoomId });
    setCallType(null); setIncomingCall(null); setCallStatus(null); setCallTargetRoomId(null);
    iceCandidateBuffer.current = [];
  };

  const endCall = () => {
    const target = getTargetSocketId();
    const duration = callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : callDuration;
    socket.emit('call:end', { targetSocketId: target, duration });
    if (duration > 0) {
      setChatMessages(prev => [...prev, { sender: 'system', text: `📞 Call ended - Duration: ${formatCallDuration(duration)}`, timestamp: new Date().toISOString(), isCallEnded: true }]);
    }
    cleanupCall();
    setCallStatus(null); setCallType(null); setCallTargetRoomId(null); setCallDuration(0); setCallStartTime(null);
    toast(`Call ended - Duration: ${formatCallDuration(duration)}`, 'blue');
  };

  // Manager send message
  const sendChatReply = async () => {
    if (!chatReply.trim() || !selectedChat) return;
    const msg = { sender: 'admin', text: chatReply, timestamp: new Date().toISOString() };
    // Optimistically add locally
    setChatMessages(prev => [...prev, msg]);
    
    // Emit to socket for real-time delivery
    socket.emit('manager:message', { roomId: selectedChat.roomId, message: chatReply });
    
    // Save message to database for persistence
    try {
      const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
      await fetch(`${API_URL}/chat-sessions/${selectedChat.roomId}/message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(msg)
      });
    } catch (error) {
      console.error('Error saving message to database:', error);
    }
    
    setChatReply('');
    setShowEmoji(false);
  };

  // Accept chat from queue and load history
  const acceptTransferredChat = async (chat) => {
    setSelectedChat(chat);
    setCurrentAcceptedChat(chat);
    modals.closeModal('queue');
    // Only open chat modal if manager is on the queue tab
    if (activeTab === 'queue') {
      modals.openModal('chat');
    }
    setChatMessages([]);
    
    // Join the room so we receive real-time messages
    socket.emit('manager:join_room', { roomId: chat.roomId });
    
    // Fetch chat session from database for persistence
    try {
      const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
      const response = await fetch(`${API_URL}/chat-sessions/${chat.roomId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const session = await response.json();
        if (session.messages && session.messages.length > 0) {
          setChatMessages(session.messages);
        }
        
        // Update session status to active in database
        await fetch(`${API_URL}/chat-sessions/${chat.roomId}/status`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            status: 'active', 
            agentId: currentUser?._id, 
            agentName: currentUser?.fullName 
          })
        });
      }
    } catch (error) {
      console.error('Error fetching chat session:', error);
      // Fallback to socket if API fails
      socket.emit('manager:get_history', { roomId: chat.roomId }, (response) => {
        if (response?.success && response.messages) {
          setChatMessages(response.messages);
        }
      });
    }
    
    // Move chat from pending to accepted (handle both transferred chats and escalated active chats)
    setTransferredChats(prev => prev.filter(c => c.roomId !== chat.roomId));
    setEscalatedActiveChats(prev => prev.filter(c => c.roomId !== chat.roomId));
    // Check if chat is already in acceptedChats to avoid duplicates
    setAcceptedChats(prev => {
      if (prev.some(c => c.roomId === chat.roomId)) return prev;
      return [...prev, { ...chat, acceptedAt: new Date().toISOString(), status: 'accepted' }];
    });
    notifications.show({
      title: 'Chat Accepted',
      message: `You are now chatting with ${chat.clientName}`,
      color: 'green',
      autoClose: 3000
    });
  };

  // Resolve an accepted chat — mirrors AdminDashboard's resolve flow
  const resolveChat = async (roomId) => {
    const chat = acceptedChats.find(c => c.roomId === roomId);

    // Emit resolve via socket so the server marks it resolved, sends system message to client,
    // and broadcasts to all admin dashboards
    socket.emit('admin:resolve', { roomId, resolved: true, resolvedAt: new Date().toISOString() });

    if (chat) {
      setEscalationHistory(prev => [{
        ...chat,
        completedAt: new Date().toISOString(),
        status: 'completed',
        messageCount: chatMessages.length,
      }, ...prev]);
    }

    toast('Marked as resolved - customer can now rate the chat', 'green');

    // Auto-close the chat panel after a short delay (same pattern as AdminDashboard)
    setTimeout(() => {
      setAcceptedChats(prev => prev.filter(c => c.roomId !== roomId));
      if (selectedChat?.roomId === roomId) {
        modals.closeModal('chat');
        setSelectedChat(null);
        setCurrentAcceptedChat(null);
        setChatMessages([]);
      }
    }, 2000);
  };

  // Token management
  const [token, setToken] = useState(localStorage.getItem(`${roleConfig.storagePrefix}Token`));

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  /* ── API Calls ── */
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/stats`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      // If a specific role filter is selected, use it; otherwise send all visible roles
      const roleParam = userRoleFilter !== 'all' 
        ? `&role=${userRoleFilter}` 
        : `&roles=${roleConfig.visibleUserRoles.join(',')}`;
      const res = await fetch(
        `${API_URL}/users?page=${usersPage}&limit=10&search=${userSearch}${roleParam}`,
        { headers: authHeaders }
      );
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setUsersTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, [usersPage, userSearch, userRoleFilter, token, roleConfig]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_URL}/clients?page=${clientsPage}&limit=10&search=${clientSearch}`,
        { headers: authHeaders }
      );
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients);
        setClientsTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  }, [clientsPage, clientSearch, token]);

  const fetchTransactions = useCallback(async () => {
    try {
      setTransactionsLoading(true);
      const statusParam = transactionStatusFilter !== 'all' ? `&status=${transactionStatusFilter}` : '';
      const res = await fetch(
        `${API_URL}/call-transactions?page=${transactionsPage}&limit=10&search=${transactionSearch}${statusParam}`,
        { headers: authHeaders }
      );
      if (res.ok) {
        const data = await res.json();
        setTransactions(data.transactions);
        setTransactionsTotal(data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      toast('Error fetching call transactions', 'red');
    } finally {
      setTransactionsLoading(false);
    }
  }, [transactionsPage, transactionSearch, transactionStatusFilter, token]);

  // Helpdesk performance data (for manager/supervisor)
  const [helpdeskPerf, setHelpdeskPerf] = useState(null);
  const [perfLoading, setPerfLoading] = useState(false);

  const fetchHelpdeskPerformance = useCallback(async () => {
    setPerfLoading(true);
    try {
      const res = await fetch(`${API_URL}/helpdesk-performance`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setHelpdeskPerf(data);
      }
    } catch (err) {
      console.error('Error fetching helpdesk performance:', err);
    }
    setPerfLoading(false);
  }, [token]);

  // Fetch roles from API
  const fetchRoles = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/roles`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setRoles(data);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  }, [token]);

  // Fetch permissions from API
  const fetchPermissions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/permissions`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        console.log(data);
        setPermissions(data);
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchStats();
      // Also fetch helpdesk performance for manager/supervisor
      if (propRole === 'manager' || propRole === 'supervisor') {
        fetchHelpdeskPerformance();
      }
    } else if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'clients') {
      fetchClients();
    } else if (activeTab === 'transactions') {
      fetchTransactions();
    } else if (activeTab === 'roles') {
      fetchRoles();
      fetchPermissions();
    } else if (activeTab === 'permissions') {
      fetchPermissions();
    }
  }, [activeTab, usersPage, clientsPage, userSearch, userRoleFilter, clientSearch, transactionsPage, transactionSearch, transactionStatusFilter, fetchStats, fetchUsers, fetchClients, fetchTransactions, fetchHelpdeskPerformance, fetchRoles, fetchPermissions, propRole]);

  // Fetch roles on mount to check user permissions
  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  // Helper function to check if current user has permission to create users based on their role
  // Simplified fixed functions per role:
  // - Super Admin: can create all user types (manager, supervisor, helpdesk_agent)
  // - Manager: can only create helpdesk_agent
  // - Supervisor: can only create helpdesk_agent (and optionally manager)
  const canUserCreateUser = useCallback(() => {
    if (!currentUser || !currentUser.role) return false;
    
    // Super admin can create any user type
    if (currentUser.role === 'super_admin') return true;
    
    // Manager can create helpdesk agents
    if (currentUser.role === 'manager') return true;
    
    // Supervisor can create helpdesk agents (and optionally managers based on original config)
    if (currentUser.role === 'supervisor') return true;
    
    return false;
  }, [currentUser]);

  // Helper function to check if current user has permission to edit users based on their role
  const canUserEditUser = useCallback(() => {
    if (!currentUser || !currentUser.role) return false;
    
    // Super admin can edit any user
    if (currentUser.role === 'super_admin') return true;
    
    // Manager can edit helpdesk agents
    if (currentUser.role === 'manager') return true;
    
    // Supervisor can edit helpdesk agents
    if (currentUser.role === 'supervisor') return true;
    
    return false;
  }, [currentUser]);

  // Helper function to check if current user has permission to delete users based on their role
  const canUserDeleteUser = useCallback(() => {
    if (!currentUser || !currentUser.role) return false;
    
    // Super admin can delete any user
    if (currentUser.role === 'super_admin') return true;
    
    // Manager cannot delete users (based on original config)
    if (currentUser.role === 'manager') return false;
    
    // Supervisor cannot delete users (based on original config)
    if (currentUser.role === 'supervisor') return false;
    
    return false;
  }, [currentUser]);

  /* ── User Management ── */
  const handleUserSubmit = async () => {
    console.log('Submitting user data:', userForm);
    try {
      const mode = userModalState.data?.mode || 'create';
      const userId = userModalState.data?.userId;
      
      const url = mode === 'create'
        ? `${API_URL}/users`
        : `${API_URL}/users/${userId}`;
      
      const method = mode === 'create' ? 'POST' : 'PUT';
      
      // Prepare the data to send - ensure permissions are properly formatted
      const dataToSend = { ...userForm };
      
      // Ensure permissions object has proper boolean values
      if (dataToSend.permissions) {
        dataToSend.permissions = {
          canManageUsers: Boolean(dataToSend.permissions.canManageUsers),
          canManageClients: Boolean(dataToSend.permissions.canManageClients),
          canViewReports: Boolean(dataToSend.permissions.canViewReports),
          canManageSettings: Boolean(dataToSend.permissions.canManageSettings),
          canHandleChats: dataToSend.permissions.canHandleChats !== undefined ? Boolean(dataToSend.permissions.canHandleChats) : true
        };
      }
      console.log('Sending data to API:', dataToSend);
      
      // Only include password if creating new user or if password field is filled
      if (mode === 'edit' && !dataToSend.password) {
        delete dataToSend.password;
      }
      
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(dataToSend)
      });
      
      const result = await res.json();
      console.log('API response:', result);
      
      if (res.ok) {
        toast(mode === 'create' ? 'User created successfully' : 'User updated successfully', 'green');
        modals.closeModal('user');
        setUserForm({ 
          username: '', email: '', password: '', fullName: '', 
          role: 'helpdesk_agent', department: '', phone: '',
          status: 'active',
          permissions: {
            canManageUsers: false,
            canManageClients: false,
            canViewReports: false,
            canManageSettings: false,
            canHandleChats: true
          }
        });
        fetchUsers();
      } else {
        toast(result.error || 'Error saving user', 'red');
      }
    } catch (err) {
      console.error('Error submitting user:', err);
      toast('Error saving user', 'red');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      const res = await fetch(`${API_URL}/users/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      
      if (res.ok) {
        toast('User deleted successfully', 'green');
        fetchUsers();
      } else {
        const err = await res.json();
        toast(err.error || 'Error deleting user', 'red');
      }
    } catch (err) {
      toast('Error deleting user', 'red');
    }
  };

  const openUserModal = (mode, user = null) => {
    if (user) {
      setUserForm({
        username: user.username || '',
        email: user.email || '',
        password: '',
        fullName: user.fullName || '',
        role: user.role || 'helpdesk_agent',
        department: user.department || '',
        phone: user.phone || '',
        status: user.status || 'active',
        permissions: user.permissions || {
          canManageUsers: false,
          canManageClients: false,
          canViewReports: false,
          canManageSettings: false,
          canHandleChats: true
        }
      });
    } else {
      // For supervisors, default to helpdesk_agent only
      const defaultRole = propRole === 'supervisor' ? 'helpdesk_agent' : 'helpdesk_agent';
      setUserForm({ 
        username: '', email: '', password: '', fullName: '', 
        role: defaultRole, department: '', phone: '',
        status: 'active',
        permissions: {
          canManageUsers: false,
          canManageClients: false,
          canViewReports: false,
          canManageSettings: false,
          canHandleChats: true
        }
      });
    }
    // Open modal - this will close others automatically
    modals.openModal('user', { mode, userId: user?._id || null });
  };

  // Get role options based on current user's role
  const getRoleOptions = () => {
    const allRoles = [
      { value: 'helpdesk_agent', label: 'Help Desk Agent' },
      { value: 'supervisor', label: 'Supervisor' },
      { value: 'manager', label: 'Manager' },
      { value: 'super_admin', label: 'Super Admin' }
    ];
    
    if (propRole === 'supervisor') {
      // Supervisors can only create agents and managers
      return allRoles.filter(r => ['helpdesk_agent', 'manager'].includes(r.value));
    } else if (propRole === 'manager') {
      // Managers can only create agents
      return allRoles.filter(r => r.value === 'helpdesk_agent');
    }
    
    return allRoles;
  };

  /* ── Client Management ── */
  const handleClientSubmit = async () => {
    try {
      const mode = clientModalState.data?.mode || 'create';
      const clientId = clientModalState.data?.clientId;
      
      const url = mode === 'create'
        ? `${API_URL}/clients`
        : `${API_URL}/clients/${clientId}`;
      
      const method = mode === 'create' ? 'POST' : 'PUT';
      
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(clientForm)
      });
      
      if (res.ok) {
        toast(mode === 'create' ? 'Client created successfully' : 'Client updated successfully', 'green');
        modals.closeModal('client');
        setClientForm({ firstName: '', lastName: '', email: '', phone: '', company: '', notes: '' });
        fetchClients();
      } else {
        const err = await res.json();
        toast(err.error || 'Error saving client', 'red');
      }
    } catch (err) {
      toast('Error saving client', 'red');
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    
    try {
      const res = await fetch(`${API_URL}/clients/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      
      if (res.ok) {
        toast('Client deleted successfully', 'green');
        fetchClients();
      } else {
        const err = await res.json();
        toast(err.error || 'Error deleting client', 'red');
      }
    } catch (err) {
      toast('Error deleting client', 'red');
    }
  };
  
  const handleDeleteRole = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      const res = await fetch(`${API_URL}/roles/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        setRoles(roles.filter(r => r.id !== id));
        toast('Role deleted successfully', 'green');
      } else {
        const err = await res.json();
        toast(err.error || 'Error deleting role', 'red');
      }
    } catch (err) {
      console.error('Error deleting role:', err);
      toast('Error deleting role', 'red');
    }
  };
  
  const handleLogout = () => {
    const userData = JSON.parse(localStorage.getItem(`${roleConfig.storagePrefix}User`) || '{}');
    
    // Emit status change to offline and update DB before navigating
    if (userData._id) {
      socket.emit('agent:status_change', { userId: userData._id, status: 'offline' }, () => {
        localStorage.removeItem(`${roleConfig.storagePrefix}Token`);
        localStorage.removeItem(`${roleConfig.storagePrefix}User`);
        navigate('/login');
      });
      // Also call the logout API to update DB status
      const token = localStorage.getItem(`${roleConfig.storagePrefix}Token`);
      fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      }).catch(() => {});
      // Fallback: navigate after 500ms if server doesn't acknowledge
      setTimeout(() => {
        localStorage.removeItem(`${roleConfig.storagePrefix}Token`);
        localStorage.removeItem(`${roleConfig.storagePrefix}User`);
        navigate('/login');
      }, 500);
    } else {
      localStorage.removeItem(`${roleConfig.storagePrefix}Token`);
      localStorage.removeItem(`${roleConfig.storagePrefix}User`);
      navigate('/login');
    }
  };

  const handleToggleMyStatus = async () => {
    const currentIsOnline = myStatus === 'online';
    const newApiStatus = currentIsOnline ? 'away' : 'active';
    const newUiStatus = currentIsOnline ? 'away' : 'online';
    
    try {
      const res = await fetch(`${API_URL}/auth/status`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ status: newApiStatus })
      });
      if (!res.ok) throw new Error('Failed to update status');
      
      setMyStatus(newUiStatus);
      
      // Update localStorage
      const userData = JSON.parse(localStorage.getItem(`${roleConfig.storagePrefix}User`) || '{}');
      userData.status = newApiStatus;
      localStorage.setItem(`${roleConfig.storagePrefix}User`, JSON.stringify(userData));
      
      // Emit via socket for real-time updates
      socket.emit('agent:status_change', { userId: userData._id, status: newApiStatus });
      
      toast(newUiStatus === 'online' ? 'You are now online' : 'You are now away', newUiStatus === 'online' ? 'green' : 'orange');
    } catch (err) {
      toast(err.message, 'red');
    }
  };

  const handleOpenEditProfile = () => {
    setEditProfileForm({
      fullName: currentUser?.fullName || '',
      phone: currentUser?.phone || '',
      department: currentUser?.department || ''
    });
    setEditProfileOpen(true);
  };

  const handleEditProfile = async () => {
    console.log('Submitting profile edit with data:', editProfileForm);
    try {
      const res = await fetch(`${API_URL}/auth/profile`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(editProfileForm)
      });

      if (!res.ok) throw new Error('Failed to update profile');

      const updatedUser = await res.json();
      
      const merged = {
        ...currentUser,
        fullName: updatedUser.fullName,
        phone: editProfileForm.phone,
        department: updatedUser.department
      };
      setCurrentUser(merged);
      
      localStorage.setItem(`${roleConfig.storagePrefix}User`, JSON.stringify(merged));
      
      setEditProfileOpen(false);
      setProfileOpen(false);
      toast('Profile updated successfully', 'green');
    } catch (err) {
      toast(err.message, 'red');
    }
  };
  
  const handleRoleSubmit = async () => {
    try {
      const mode = roleModalState.data?.mode || 'create';
      
      const url = mode === 'create'
        ? `${API_URL}/roles`
        : `${API_URL}/roles/${roleForm.id}`;
      
      const method = mode === 'create' ? 'POST' : 'PUT';
      
      const roleData = {
        ...roleForm,
        id: roleForm.id || roleForm.name.toLowerCase().replace(/\s+/g, '_'),
        permissions: roleForm.permissions || []
      };
      
      console.log('Submitting role data to API:', { url, method, roleData });
      
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(roleData)
      });
      
      const result = await res.json();
      console.log('API response:', result);
      
      if (res.ok) {
        if (mode === 'create') {
          setRoles([...roles, roleData]);
          toast('Role created successfully', 'green');
        } else {
          setRoles(roles.map(r => r.id === roleForm.id ? roleData : r));
          toast('Role updated successfully', 'green');
        }
        modals.closeModal('role');
      } else {
        toast(result.error || 'Error saving role', 'red');
      }
    } catch (err) {
      console.error('Error submitting role:', err);
      toast('Error saving role', 'red');
    }
  };
  
  // Permission management functions
  const handlePermissionSubmit = async () => {
    try {
      const mode = permissionModalState.data?.mode || 'create';
      
      const url = mode === 'create'
        ? `${API_URL}/permissions`
        : `${API_URL}/permissions/${permissionForm.id}`;
      
      const method = mode === 'create' ? 'POST' : 'PUT';
      
      const permData = {
        ...permissionForm,
        id: permissionForm.id || permissionForm.name.toLowerCase().replace(/\s+/g, '_')
      };
      
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(permData)
      });
      
      if (res.ok) {
        const result = await res.json();
        if (mode === 'create') {
          fetchPermissions(); // Refresh permissions list to get new ID
          toast('Permission created successfully', 'green');
        } else {
          setPermissions(permissions.map(p => p.id === permissionForm.id ? result.permission : p));
          toast('Permission updated successfully', 'green');
        }
        modals.closeModal('permission');
      } else {
        const err = await res.json();
        toast(err.error || 'Error saving permission', 'red');
      }
    } catch (err) {
      console.error('Error saving permission:', err);
      toast('Error saving permission', 'red');
    }
  };
  
  const handleDeletePermission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this permission?')) return;
    try {
      const res = await fetch(`${API_URL}/permissions/${id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (res.ok) {
        setPermissions(permissions.filter(p => p.id !== id));
        toast('Permission deleted successfully', 'green');
      } else {
        const err = await res.json();
        toast(err.error || 'Error deleting permission', 'red');
      }
    } catch (err) {
      console.error('Error deleting permission:', err);
      toast('Error deleting permission', 'red');
    }
  };

  const togglePermissionActive = async (id) => {
    const permission = permissions.find(p => p.id === id);
    if (!permission) return;
    
    try {
      const res = await fetch(`${API_URL}/permissions/${id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ ...permission, active: !permission.active })
      });
      
      if (res.ok) {
        const result = await res.json();
        setPermissions(permissions.map(p => p.id === id ? result.permission : p));
        toast('Permission updated', 'green');
      } else {
        const err = await res.json();
        toast(err.error || 'Error updating permission', 'red');
      }
    } catch (err) {
      console.error('Error updating permission:', err);
      toast('Error updating permission', 'red');
    }
  };

  const openClientModal = (mode, client = null) => {
    if (client) {
      setClientForm({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
        email: client.email || '',
        phone: client.phone || '',
        company: client.company || '',
        notes: client.notes || ''
      });
    } else {
      setClientForm({ firstName: '', lastName: '', email: '', phone: '', company: '', notes: '' });
    }
    modals.openModal('client', { mode, clientId: client?._id || null });
  };

  /* ── Render ── */
  return (
    <Box className={classes.root}>
      <LoadingOverlay visible={loading} />
      
      {/* Header */}
      <Box className={classes.header}>
        <Group h="100%" px="lg" justify="space-between">
          <Group gap="xs">
            <IconShield size={24} color="#9381ff" />
            <Text fw={700} c="#2d2d2d" size="lg">{roleConfig.panelTitle}</Text>
            {(propRole === 'manager' || propRole === 'supervisor') && (
              <Group gap="xs" ml="md">
                {transferredChats.length > 0 && (
                  <Badge color="orange" size="md" variant="dot" style={{ cursor: 'pointer' }} onClick={() => {
                    setActiveTab('queue');
                  }}>
                    {transferredChats.length} Pending
                  </Badge>
                )}
                {acceptedChats.length > 0 && (
                  <Badge color="green" size="md" variant="dot" style={{ cursor: 'pointer' }} onClick={() => {
                    setActiveTab('queue');
                  }}>
                    {acceptedChats.length} Active
                  </Badge>
                )}
              </Group>
            )}
          </Group>
          <Group gap="sm">
            <TextInput
              placeholder="Search..."
              leftSection={<IconSearch size={14} />}
              size="xs"
              w={200}
              styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
            />
            <Tooltip label="My Profile">
              <ActionIcon variant="subtle" color="gray" size="lg" onClick={() => setProfileOpen(true)}>
                <Indicator size={9} color="green" offset={2} position="bottom-end" withBorder>
                  <Avatar size={32} variant="gradient" gradient={roleConfig.gradient} radius="xl">
                    {(currentUser?.fullName || 'SA').charAt(0).toUpperCase()}
                  </Avatar>
                </Indicator>
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {/* Body */}
      <Box className={classes.body}>
        <Grid gutter="md">
          {/* Sidebar */}
          <Grid.Col span={2}>
            <Paper className={classes.sidebar} p="md">
              <Stack gap="xs">
                {roleConfig.tabs.includes('dashboard') && (
                  <Button
                    variant={activeTab === 'dashboard' ? 'light' : 'subtle'}
                    color={activeTab === 'dashboard' ? 'blue' : 'gray'}
                    leftSection={<IconChartBar size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('dashboard')}
                  >
                    Dashboard
                  </Button>
                )}
                {roleConfig.tabs.includes('users') && (
                  <Button
                    variant={activeTab === 'users' ? 'light' : 'subtle'}
                    color={activeTab === 'users' ? 'blue' : 'gray'}
                    leftSection={<IconUsers size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('users')}
                  >
                    Users / Agents
                  </Button>
                )}
                {roleConfig.tabs.includes('clients') && (
                  <Button
                    variant={activeTab === 'clients' ? 'light' : 'subtle'}
                    color={activeTab === 'clients' ? 'blue' : 'gray'}
                    leftSection={<IconUser size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('clients')}
                  >
                    Clients
                  </Button>
                )}
                {roleConfig.tabs.includes('transactions') && (
                  <Button
                    variant={activeTab === 'transactions' ? 'light' : 'subtle'}
                    color={activeTab === 'transactions' ? 'blue' : 'gray'}
                    leftSection={<IconPhone size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('transactions')}
                  >
                    Transactions
                  </Button>
                )}
                {roleConfig.tabs.includes('queue') && (
                  <Button
                    variant={activeTab === 'queue' ? 'light' : 'subtle'}
                    color={activeTab === 'queue' ? 'blue' : 'gray'}
                    leftSection={
                      <Group gap="xs">
                        <IconInbox size={18} />
                        {transferredChats.filter(c => c.status === 'pending').length > 0 && (
                          <Badge size="sm" color="red" variant="filled">
                            {transferredChats.filter(c => c.status === 'pending').length}
                          </Badge>
                        )}
                      </Group>
                    }
                    justify="flex-start"
                    onClick={() => {
                      setActiveTab('queue');
                    }}
                  >
                    Queue
                  </Button>
                )}
                {roleConfig.tabs.includes('roles') && (
                  <Button
                    variant={activeTab === 'roles' ? 'light' : 'subtle'}
                    color={activeTab === 'roles' ? 'blue' : 'gray'}
                    leftSection={<IconShield size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('roles')}
                  >
                    Roles
                  </Button>
                )}
                {roleConfig.tabs.includes('permissions') && (
                  <Button
                    variant={activeTab === 'permissions' ? 'light' : 'subtle'}
                    color={activeTab === 'permissions' ? 'blue' : 'gray'}
                    leftSection={<IconLock size={18} />}
                    justify="flex-start"
                    onClick={() => setActiveTab('permissions')}
                  >
                    Permissions
                  </Button>
                )}
                <Divider my="sm" />
                {roleConfig.tabs.includes('roles') && (
                  <Button
                    variant="subtle"
                    color="gray"
                    leftSection={<IconSettings size={18} />}
                    justify="flex-start"
                    onClick={() => toast('Settings coming soon')}
                  >
                    Settings
                  </Button>
                )}
                <Button
                  variant="subtle"
                  color="red"
                  leftSection={<IconLogout size={18} />}
                  justify="flex-start"
                  onClick={handleLogout}
                >
                  Logout
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Main Content */}
          <Grid.Col span={10}>
            {/* Dashboard Tab */}
            {(activeTab === 'dashboard' || !activeTab) && (
              <>
              {/* ── MANAGER / SUPERVISOR: Helpdesk Performance Dashboard ── */}
              {(propRole === 'manager' || propRole === 'supervisor') ? (
                <Stack gap="md">
                  {/* Header */}
                  <Group justify="space-between" align="center">
                    <Group gap="sm">
                      <Avatar size={40} radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                        <IconHeadset size={22} />
                      </Avatar>
                      <div>
                        <Text fw={700} size="lg" c="#2d2d2d">Helpdesk Performance</Text>
                        <Text size="xs" c="dimmed">Real-time agent metrics & team overview</Text>
                      </div>
                    </Group>
                    <Group gap="xs">
                      <Tooltip label="Refresh data">
                        <ActionIcon variant="light" color="blue" radius="xl" size="lg" loading={perfLoading}
                          onClick={() => { fetchHelpdeskPerformance(); fetchStats(); }}>
                          <IconRefresh size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Group>

                  {/* ── Team Overview Cards ── */}
                  <SimpleGrid cols={4} spacing="sm">
                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Avatar size={38} radius="xl" variant="gradient" gradient={{ from: '#4361d8', to: '#6b4fc4' }}>
                          <IconUsers size={18} />
                        </Avatar>
                        <Badge size="xs" color="blue" variant="light" radius="xl">Team</Badge>
                      </Group>
                      <Text size="xl" fw={800} c="#2d2d2d" lh={1}>{helpdeskPerf?.team?.totalAgents || 0}</Text>
                      <Text size="xs" c="dimmed" mt={4}>Total Agents</Text>
                      <Group gap="xs" mt="xs">
                        <Badge size="xs" variant="dot" color="green">{helpdeskPerf?.team?.onlineAgents || 0} Online</Badge>
                        <Badge size="xs" variant="dot" color="orange">{helpdeskPerf?.team?.awayAgents || 0} Away</Badge>
                      </Group>
                    </Paper>

                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Avatar size={38} radius="xl" variant="gradient" gradient={{ from: '#22c55e', to: '#10b981' }}>
                          <IconPlayerPlay size={18} />
                        </Avatar>
                        <Badge size="xs" color="green" variant="light" radius="xl">Live</Badge>
                      </Group>
                      <Text size="xl" fw={800} c="#2d2d2d" lh={1}>{helpdeskPerf?.team?.activeChats || stats?.chats?.active || 0}</Text>
                      <Text size="xs" c="dimmed" mt={4}>Active Chats</Text>
                      <Group gap="xs" mt="xs">
                        <Badge size="xs" variant="dot" color="yellow">{stats?.chats?.queued || 0} Queued</Badge>
                      </Group>
                    </Paper>

                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Avatar size={38} radius="xl" variant="gradient" gradient={{ from: '#f59e0b', to: '#f97316' }}>
                          <IconCalendar size={18} />
                        </Avatar>
                        <Badge size="xs" color="orange" variant="light" radius="xl">Today</Badge>
                      </Group>
                      <Text size="xl" fw={800} c="#2d2d2d" lh={1}>{helpdeskPerf?.team?.todayChats || stats?.metrics?.todayChats || 0}</Text>
                      <Text size="xs" c="dimmed" mt={4}>Today's Chats</Text>
                      <Group gap="xs" mt="xs">
                        <Badge size="xs" variant="dot" color="blue">{helpdeskPerf?.team?.weekChats || 0} This Week</Badge>
                      </Group>
                    </Paper>

                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Avatar size={38} radius="xl" variant="gradient" gradient={{ from: '#8b5cf6', to: '#a855f7' }}>
                          <IconTarget size={18} />
                        </Avatar>
                        <Badge size="xs" color="violet" variant="light" radius="xl">Rate</Badge>
                      </Group>
                      <Text size="xl" fw={800} c="#2d2d2d" lh={1}>{helpdeskPerf?.team?.resolutionRate || stats?.metrics?.resolutionRate || 0}%</Text>
                      <Text size="xs" c="dimmed" mt={4}>Resolution Rate</Text>
                      <Progress value={helpdeskPerf?.team?.resolutionRate || 0} color="violet" size="sm" radius="xl" mt="xs" />
                    </Paper>
                  </SimpleGrid>

                  {/* ── Team Performance Metrics ── */}
                  <SimpleGrid cols={3} spacing="sm">
                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group gap="sm" align="center" mb="sm">
                        <Avatar size={32} color="orange" radius="xl" variant="light">
                          <IconClock size={16} />
                        </Avatar>
                        <Text size="sm" fw={600} c="#2d2d2d">Avg Handle Time</Text>
                      </Group>
                      <Text size="2rem" fw={800} c="orange" lh={1}>
                        {(() => {
                          const s = helpdeskPerf?.team?.avgHandlingTime || stats?.metrics?.avgHandlingTime || 0;
                          if (s < 60) return `${s}s`;
                          return `${Math.floor(s / 60)}m ${s % 60}s`;
                        })()}
                      </Text>
                      <Text size="xs" c="dimmed" mt="xs">Average time to close a chat</Text>
                    </Paper>

                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group gap="sm" align="center" mb="sm">
                        <Avatar size={32} color="yellow" radius="xl" variant="light">
                          <IconStar size={16} />
                        </Avatar>
                        <Text size="sm" fw={600} c="#2d2d2d">Satisfaction Score</Text>
                      </Group>
                      <Group align="flex-end" gap="xs">
                        <Text size="2rem" fw={800} c="yellow" lh={1}>
                          {helpdeskPerf?.team?.avgSatisfaction || stats?.metrics?.avgSatisfaction || 0}
                        </Text>
                        <Text size="sm" c="dimmed" mb={2}>/5</Text>
                      </Group>
                      <Progress value={(helpdeskPerf?.team?.avgSatisfaction || 0) * 20} color="yellow" size="sm" radius="xl" mt="xs" />
                    </Paper>

                    <Paper className={classes.perfCard} p="md" radius="lg">
                      <Group gap="sm" align="center" mb="sm">
                        <Avatar size={32} color="green" radius="xl" variant="light">
                          <IconCircleCheck size={16} />
                        </Avatar>
                        <Text size="sm" fw={600} c="#2d2d2d">Chats Overview</Text>
                      </Group>
                      <Group gap="lg">
                        <div>
                          <Text size="xl" fw={800} c="green" lh={1}>{helpdeskPerf?.team?.resolvedChats || stats?.chats?.closed || 0}</Text>
                          <Text size="xs" c="dimmed" mt={2}>Resolved</Text>
                        </div>
                        <Divider orientation="vertical" color="dark.4" />
                        <div>
                          <Text size="xl" fw={800} c="blue" lh={1}>{helpdeskPerf?.team?.totalChats || stats?.chats?.total || 0}</Text>
                          <Text size="xs" c="dimmed" mt={2}>Total</Text>
                        </div>
                        <Divider orientation="vertical" color="dark.4" />
                        <div>
                          <Text size="xl" fw={800} c="red" lh={1}>{helpdeskPerf?.team?.closedChats - helpdeskPerf?.team?.resolvedChats || 0}</Text>
                          <Text size="xs" c="dimmed" mt={2}>Unresolved</Text>
                        </div>
                      </Group>
                    </Paper>
                  </SimpleGrid>

                  {/* ── Agent Performance Table ── */}
                  <Paper className={classes.perfCard} p="md" radius="lg">
                    <Group justify="space-between" mb="md">
                      <Group gap="sm">
                        <Avatar size={28} radius="xl" color="blue" variant="light">
                          <IconChartLine size={14} />
                        </Avatar>
                        <Text fw={700} size="sm" c="#2d2d2d">Agent Performance Leaderboard</Text>
                      </Group>
                      <Badge size="sm" variant="light" color="blue" radius="xl">
                        {helpdeskPerf?.agents?.length || 0} Agents
                      </Badge>
                    </Group>

                    {(!helpdeskPerf?.agents || helpdeskPerf.agents.length === 0) ? (
                      <Center py="xl">
                        <Stack align="center" gap="xs">
                          <Avatar size={48} radius="xl" color="gray" variant="light">
                            <IconHeadset size={24} />
                          </Avatar>
                          <Text size="sm" c="dimmed">No helpdesk agents found</Text>
                          <Text size="xs" c="dimmed">Create agents from the Users tab</Text>
                        </Stack>
                      </Center>
                    ) : (
                      <ScrollArea>
                        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Agent</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Today</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Total</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Active</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Resolved</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Avg Handle</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Avg Response</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Rating</Table.Th>
                              <Table.Th style={{ color: '#9895b5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' }}>Resolution</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {helpdeskPerf.agents
                              .sort((a, b) => (b.performance?.totalChats || 0) - (a.performance?.totalChats || 0))
                              .map((agent, idx) => {
                                const perf = agent.performance || {};
                                const handleTime = perf.avgHandlingTime || 0;
                                const respTime = perf.avgResponseTime || 0;
                                const statusColor = agent.status === 'active' ? 'green' : agent.status === 'away' ? 'orange' : 'gray';
                                const initial = (agent.fullName || '?').charAt(0).toUpperCase();
                                
                                // Check if agent is connected via socket (real-time)
                                const isOnlineNow = agentStatuses.find(s => s.userId === agent._id);

                                return (
                                  <Table.Tr key={agent._id} style={{ background: idx % 2 === 0 ? 'rgba(26, 31, 46, 0.3)' : 'transparent' }}>
                                    <Table.Td>
                                      <Group gap="sm" wrap="nowrap">
                                        <Indicator position="bottom-end" size={10} offset={3} withBorder
                                          color={isOnlineNow ? (isOnlineNow.status === 'active' ? 'green' : 'orange') : 'gray'}>
                                          <Avatar size={36} radius="xl" color={COLORS[idx % COLORS.length]} variant="filled">
                                            {initial}
                                          </Avatar>
                                        </Indicator>
                                        <div>
                                          <Text size="sm" fw={600} c="#2d2d2d" lh={1.2}>{agent.fullName}</Text>
                                          <Text size="xs" c="dimmed" lh={1.2}>{agent.department || 'Support'}</Text>
                                        </div>
                                      </Group>
                                    </Table.Td>
                                    <Table.Td>
                                      <Badge size="xs" radius="xl" variant="light"
                                        color={isOnlineNow ? (isOnlineNow.status === 'active' ? 'green' : 'orange') : 'gray'}>
                                        {isOnlineNow ? (isOnlineNow.status === 'active' ? 'Online' : 'Away') : 'Offline'}
                                      </Badge>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Text fw={700} size="sm" c="blue">{perf.todayChats || 0}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Text fw={700} size="sm" c="#2d2d2d">{perf.totalChats || 0}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Badge size="sm" variant="light" color="cyan" radius="xl">{perf.activeChats || 0}</Badge>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Text fw={600} size="sm" c="green">{perf.resolvedChats || 0}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Text size="xs" c="orange" fw={600}>
                                        {handleTime < 60 ? `${handleTime}s` : `${Math.floor(handleTime / 60)}m ${handleTime % 60}s`}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Text size="xs" c="cyan" fw={600}>
                                        {respTime < 60 ? `${respTime}s` : `${Math.floor(respTime / 60)}m ${respTime % 60}s`}
                                      </Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <Group gap={2} justify="center">
                                        <IconStar size={12} color="#f59e0b" fill={perf.avgSatisfaction > 0 ? '#f59e0b' : 'none'} />
                                        <Text size="xs" fw={700} c="yellow">{perf.avgSatisfaction || '—'}</Text>
                                      </Group>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                      <RingProgress size={36} thickness={4} roundCaps
                                        sections={[{ value: perf.resolutionRate || 0, color: perf.resolutionRate >= 70 ? 'green' : perf.resolutionRate >= 40 ? 'yellow' : 'red' }]}
                                        label={<Text size="8px" ta="center" fw={700} c="#2d2d2d">{perf.resolutionRate || 0}%</Text>}
                                      />
                                    </Table.Td>
                                  </Table.Tr>
                                );
                              })}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    )}
                  </Paper>
                </Stack>
              ) : (
              /* ── SUPER ADMIN: Original Dashboard ── */
              <Stack gap="sm">
                <Text fw={700} size="lg" c="#2d2d2d">Dashboard Overview</Text>
                
                {/* Stat Cards Row - Horizontal Layout */}
                <Group gap="sm" grow>
                  <Paper className={`${classes.statCard} users`} p="sm">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Users</Text>
                        <Text className={classes.statNumber}>{stats && stats.users ? stats.users.total : 0}</Text>
                      </div>
                      <Avatar color="blue" radius="xl" size={36} withBorder>
                        <IconUsers size={18} stroke={1.5} />
                      </Avatar>
                    </Group>
                  </Paper>
                  
                  <Paper className={`${classes.statCard} clients`} p="sm">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Clients</Text>
                        <Text className={classes.statNumber}>{stats && stats.clients ? stats.clients.total : 0}</Text>
                      </div>
                      <Avatar color="violet" radius="xl" size={36} withBorder>
                        <IconUser size={18} stroke={1.5} />
                      </Avatar>
                    </Group>
                  </Paper>
                  
                  <Paper className={`${classes.statCard} active`} p="sm">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Active</Text>
                        <Text className={classes.statNumber}>{stats && stats.chats ? stats.chats.active : 0}</Text>
                      </div>
                      <Avatar color="green" radius="xl" size={36} withBorder>
                        <IconMessage size={18} stroke={1.5} />
                      </Avatar>
                    </Group>
                  </Paper>
                  
                  <Paper className={`${classes.statCard} closed`} p="sm">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Closed</Text>
                        <Text className={classes.statNumber}>{stats && stats.chats ? stats.chats.closed : 0}</Text>
                      </div>
                      <Avatar color="gray" radius="xl" size={36} withBorder>
                        <IconCheck size={18} stroke={1.5} />
                      </Avatar>
                    </Group>
                  </Paper>
                </Group>
                
                {/* Activity & Performance Metrics Row */}
                <SimpleGrid cols={3} spacing="sm">
                  <Card className={classes.statCard} padding="sm">
                    <Flex align="center" gap="sm">
                      <Avatar color="orange" radius="xl" size={32}>
                        <IconClock size={16} />
                      </Avatar>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase">Avg Wait Time</Text>
                        <Text size="lg" fw={700} c="orange">
                          {stats && stats.metrics ? `${stats.metrics.avgWaitTime}s` : '0s'}
                        </Text>
                      </div>
                    </Flex>
                  </Card>
                  
                  <Card className={classes.statCard} padding="sm">
                    <Flex align="center" gap="sm">
                      <Avatar color="cyan" radius="xl" size={32}>
                        <IconActivity size={16} />
                      </Avatar>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase">Avg Handle Time</Text>
                        <Text size="lg" fw={700} c="cyan">
                          {stats && stats.metrics ? `${stats.metrics.avgHandlingTime}s` : '0s'}
                        </Text>
                      </div>
                    </Flex>
                  </Card>
                  
                  <Card className={classes.statCard} padding="sm">
                    <Flex align="center" gap="sm">
                      <Avatar color="yellow" radius="xl" size={32}>
                        <IconStar size={16} />
                      </Avatar>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase">Satisfaction</Text>
                        <Group gap={2}>
                          <Text size="lg" fw={700} c="yellow">
                            {stats && stats.metrics ? stats.metrics.avgSatisfaction : '0'}
                          </Text>
                          <Text size="xs" c="dimmed">/5</Text>
                        </Group>
                      </div>
                    </Flex>
                  </Card>
                </SimpleGrid>
                
                {/* Today's Stats & Resolution Rate */}
                <SimpleGrid cols={2} spacing="sm">
                  <Card className={classes.statCard} padding="sm">
                    <Text fw={700} mb="xs" c="#2d2d2d" size="xs">Today's Activity</Text>
                    {stats && stats.metrics ? (
                      <Group gap="lg">
                        <div>
                          <Text size="xl" fw={700} c="blue">{stats.metrics.todayChats}</Text>
                          <Text size="xs" c="dimmed">Total Chats</Text>
                        </div>
                        <div>
                          <Text size="xl" fw={700} c="green">{stats.metrics.todayClosed}</Text>
                          <Text size="xs" c="dimmed">Resolved</Text>
                        </div>
                      </Group>
                    ) : (
                      <Text c="dimmed" size="sm">Loading...</Text>
                    )}
                  </Card>
                  
                  <Card className={classes.statCard} padding="sm">
                    <Text fw={700} mb="xs" c="#2d2d2d" size="xs">Performance</Text>
                    {stats && stats.chats ? (
                      <Stack gap="xs">
                        <Group justify="space-between">
                          <Text size="xs" c="#2d2d2d">Resolution Rate</Text>
                          <Text size="xs" c="green" fw={600}>
                            {stats && stats.metrics ? `${stats.metrics.resolutionRate}%` : '0%'}
                          </Text>
                        </Group>
                        <Progress 
                          value={stats && stats.metrics ? stats.metrics.resolutionRate : 0} 
                          color="green" 
                          size="sm" 
                          radius="xl" 
                        />
                        <Group justify="space-between">
                          <Text size="xs" c="#2d2d2d">Chat Activity</Text>
                          <Text size="xs" c="blue" fw={600}>
                            {stats.chats.total > 0 ? Math.round((stats.chats.active / stats.chats.total) * 100) : 0}%
                          </Text>
                        </Group>
                        <Progress 
                          value={stats.chats.total > 0 ? (stats.chats.active / stats.chats.total) * 100 : 0} 
                          color="blue" 
                          size="sm" 
                          radius="xl" 
                        />
                      </Stack>
                    ) : (
                      <Text c="dimmed" size="sm">Loading...</Text>
                    )}
                  </Card>
                </SimpleGrid>
              </Stack>
              )}
              </>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700} size="lg" c="#2d2d2d">Users & Agents Management</Text>
                  <Group>
                    <TextInput
                      placeholder="Search by name, email..."
                      leftSection={<IconSearch size={14} />}
                      value={userSearch}
                      onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                      styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                      w={220}
                    />
                    <Select
                      placeholder="Filter by role"
                      value={userRoleFilter}
                      onChange={(value) => { setUserRoleFilter(value); setUsersPage(1); }}
                      data={roleConfig.roleFilterOptions}
                      size="sm"
                      w={170}
                      styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                      allowDeselect={false}
                    />
                    {canUserCreateUser() && (
                      <Button leftSection={<IconPlus size={16} />} onClick={() => openUserModal('create')}>
                        Add User
                      </Button>
                    )}
                  </Group>
                </Group>

                <Paper className={classes.tablePaper}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th c="dimmed">User</Table.Th>
                        <Table.Th c="dimmed">Role</Table.Th>
                        <Table.Th c="dimmed">Department</Table.Th>
                        <Table.Th c="dimmed">Status</Table.Th>
                        <Table.Th c="dimmed">Last Login</Table.Th>
                        {(canUserEditUser() || canUserDeleteUser()) && <Table.Th c="dimmed">Actions</Table.Th>}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {users.map((user) => (
                        <Table.Tr key={user._id}>
                          <Table.Td>
                            <Group gap="sm">
                              <Avatar color={avatarColor(user.fullName)} radius="xl">
                                {getInitial(user.fullName)}
                              </Avatar>
                              <div>
                                <Text size="sm" fw={600} c="#2d2d2d">{user.fullName}</Text>
                                <Text size="xs" c="dimmed">{user.email}</Text>
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={user.role === 'super_admin' ? 'violet' : user.role === 'manager' ? 'blue' : user.role === 'supervisor' ? 'cyan' : 'green'} variant="light">
                              {user.role === 'super_admin' ? 'Super Admin' : user.role === 'manager' ? 'Manager' : user.role === 'supervisor' ? 'Supervisor' : 'Helpdesk Agent'}
                            </Badge>
                          </Table.Td>
                          <Table.Td><Text size="sm" c="#2d2d2d">{user.department || '-'}</Text></Table.Td>
                          <Table.Td>
                            <Group gap={6}>
                              {(() => {
                                const liveAgent = agentStatuses.find(a => a.userId === user._id);
                                const displayStatus = liveAgent ? liveAgent.status : user.status;
                                // Map DB status to display: active=Online, away=Away, inactive/suspended=Offline/Suspended
                                const statusLabel = displayStatus === 'active' ? 'Online' : displayStatus === 'away' ? 'Away' : displayStatus === 'suspended' ? 'Suspended' : 'Offline';
                                const statusColor = displayStatus === 'active' ? 'green' : displayStatus === 'away' ? 'yellow' : displayStatus === 'suspended' ? 'red' : 'gray';
                                return (
                                  <Badge color={statusColor} variant="light">
                                    {statusLabel}
                                  </Badge>
                                );
                              })()}
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="dimmed">
                              {user.lastLogin ? new Date(user.lastLogin).toLocaleString([], { year:'numeric', month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : 'Never'}
                            </Text>
                          </Table.Td>
                          {(canUserEditUser() || canUserDeleteUser()) && (
                          <Table.Td>
                            <Group gap={4}>
                              {canUserEditUser() && (
                                <Tooltip label="Edit">
                                  <ActionIcon variant="subtle" color="blue" onClick={() => openUserModal('edit', user)}>
                                    <IconEdit size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                              {canUserDeleteUser() && (
                                <Tooltip label="Delete">
                                  <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteUser(user._id)}>
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                          )}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Group justify="center" mt="md">
                    <Pagination
                      total={Math.ceil(usersTotal / 10)}
                      value={usersPage}
                      onChange={setUsersPage}
                      color="blue"
                      size="sm"
                    />
                  </Group>
                </Paper>
              </Stack>
            )}

            {/* Clients Tab */}
            {activeTab === 'clients' && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700} size="lg" c="#2d2d2d">Clients Management</Text>
                  <Group>
                    <TextInput
                      placeholder="Search clients..."
                      leftSection={<IconSearch size={14} />}
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                    />
                    {roleConfig.canCreateClient && (
                      <Button leftSection={<IconPlus size={16} />} onClick={() => openClientModal('create')}>
                        Add Client
                      </Button>
                    )}
                  </Group>
                </Group>

                <Paper className={classes.tablePaper}>
                  <Table highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th c="dimmed">Client</Table.Th>
                        <Table.Th c="dimmed">Company</Table.Th>
                        <Table.Th c="dimmed">Phone</Table.Th>
                        <Table.Th c="dimmed">Status</Table.Th>
                        <Table.Th c="dimmed">Total Chats</Table.Th>
                        {(roleConfig.canEditClient || roleConfig.canDeleteClient) && <Table.Th c="dimmed">Actions</Table.Th>}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {clients.map((client) => (
                        <Table.Tr key={client._id}>
                          <Table.Td>
                            <Group gap="sm">
                              <Avatar color={avatarColor(client.firstName)} radius="xl">
                                {getInitial(client.firstName)}
                              </Avatar>
                              <div>
                                <Text size="sm" fw={600} c="#2d2d2d">{client.firstName} {client.lastName}</Text>
                                <Text size="xs" c="dimmed">{client.email}</Text>
                              </div>
                            </Group>
                          </Table.Td>
                          <Table.Td><Text size="sm" c="#2d2d2d">{client.company || '-'}</Text></Table.Td>
                          <Table.Td><Text size="sm" c="#2d2d2d">{client.phone || '-'}</Text></Table.Td>
                          <Table.Td>
                            <Badge color={client.status === 'active' ? 'green' : client.status === 'blocked' ? 'red' : 'gray'} variant="light">
                              {client.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td><Text size="sm" c="#2d2d2d">{client.totalChats || 0}</Text></Table.Td>
                          {(roleConfig.canEditClient || roleConfig.canDeleteClient) && (
                          <Table.Td>
                            <Group gap={4}>
                              <Tooltip label="View Voucher">
                                <ActionIcon variant="subtle" color="violet" onClick={() => { 
                                  setVoucherClientEmail(client.email); 
                                  setVoucherSidebarOpen(true);
                                  toast(`Loading voucher for ${client.email}...`, 'blue');
                                }}>
                                  <IconWifi size={16} />
                                </ActionIcon>
                              </Tooltip>
                              {roleConfig.canEditClient && (
                              <Tooltip label="Edit">
                                <ActionIcon variant="subtle" color="blue" onClick={() => openClientModal('edit', client)}>
                                  <IconEdit size={16} />
                                </ActionIcon>
                              </Tooltip>
                              )}
                              {roleConfig.canDeleteClient && (
                              <Tooltip label="Delete">
                                <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteClient(client._id)}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                              )}
                            </Group>
                          </Table.Td>
                          )}
                          {!roleConfig.canEditClient && !roleConfig.canDeleteClient && (
                          <Table.Td>
                            <Tooltip label="View Voucher">
                              <ActionIcon variant="subtle" color="violet" onClick={() => { 
                                setVoucherClientEmail(client.email); 
                                setVoucherSidebarOpen(true);
                                toast(`Loading voucher for ${client.email}...`, 'blue');
                              }}>
                                <IconWifi size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Table.Td>
                          )}
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                  <Group justify="center" mt="md">
                    <Pagination
                      total={Math.ceil(clientsTotal / 10)}
                      value={clientsPage}
                      onChange={setClientsPage}
                      color="blue"
                      size="sm"
                    />
                  </Group>
                </Paper>
              </Stack>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700} size="lg" c="#2d2d2d">📊 Transactions</Text>
                  <Group>
                    <TextInput
                      placeholder="Search by name, email, phone, or agent..."
                      leftSection={<IconSearch size={14} />}
                      value={transactionSearch}
                      onChange={(e) => setTransactionSearch(e.target.value)}
                      w={300}
                      styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                    />
                    <Select
                      data={[
                        { value: 'all', label: 'All Status' },
                        { value: 'resolved', label: 'Resolved' },
                        { value: 'escalated', label: 'Escalated' },
                      ]}
                      value={transactionStatusFilter}
                      onChange={setTransactionStatusFilter}
                      styles={{ input: { background: '#f9f8fc', border: '1px solid #e8e5f0', color: '#2d2d2d' } }}
                    />
                  </Group>
                </Group>

                <Paper className={classes.tablePaper}>
                  {transactionsLoading ? (
                    <Center py="xl">
                      <LoadingOverlay visible={true} />
                    </Center>
                  ) : transactions.length === 0 ? (
                    <Box p="xl" ta="center">
                      <Text c="dimmed">No call transactions found</Text>
                    </Box>
                  ) : (
                    <>
                      <Table highlightOnHover striped>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th c="dimmed">Customer</Table.Th>
                            <Table.Th c="dimmed">Contact</Table.Th>
                            <Table.Th c="dimmed">Duration</Table.Th>
                            <Table.Th c="dimmed">Agent Handled</Table.Th>
                            <Table.Th c="dimmed">Status</Table.Th>
                            <Table.Th c="dimmed">Date/Time</Table.Th>
                            <Table.Th c="dimmed">Rating</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {transactions.map((t) => (
                            <Table.Tr key={t._id}>
                              <Table.Td>
                                <Group gap="sm">
                                  <Avatar 
                                    color={['blue', 'cyan', 'teal', 'green'].includes(COLORS[t.userName?.charCodeAt(0) % COLORS.length]) ? 'blue' : COLORS[t.userName?.charCodeAt(0) % COLORS.length]}
                                    radius="xl" 
                                    size={32}
                                  >
                                    {t.userName?.charAt(0).toUpperCase() || '?'}
                                  </Avatar>
                                  <Box>
                                    <Text size="sm" fw={500} c="#2d2d2d">{t.userName || 'Unknown'}</Text>
                                    <Text size="xs" c="dimmed">{t.roomId}</Text>
                                  </Box>
                                </Group>
                              </Table.Td>
                              <Table.Td>
                                <Box>
                                  {t.customerEmail && <Text size="xs" c="dimmed">{t.customerEmail}</Text>}
                                  {t.customerPhone && <Text size="xs" c="dimmed">{t.customerPhone}</Text>}
                                  {!t.customerEmail && !t.customerPhone && <Text size="xs" c="dimmed">-</Text>}
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm">
                                  {t.callDuration > 0 
                                    ? `${Math.floor(t.callDuration / 60)}m ${t.callDuration % 60}s`
                                    : t.createdAt || t.closedAt
                                      ? `${Math.floor((new Date(t.closedAt || t.createdAt) - new Date(t.joinedAt)) / 60000)}m`
                                      : '-'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Box>
                                  <Text size="sm" fw={500} c="#2d2d2d">{t.agentName || 'Unassigned'}</Text>
                                  {t.agentEmail && <Text size="xs" c="dimmed">{t.agentEmail}</Text>}
                                </Box>
                              </Table.Td>
                              <Table.Td>
                                <Stack gap="xs">
                                  <Badge 
                                    color={t.status === 'Resolved' ? 'green' : 'orange'}
                                    variant="dot"
                                  >
                                    {t.status === 'Resolved' ? '✅' : '⬆️'} {t.status}
                                  </Badge>
                                  {t.rating && (
                                    <Badge color="yellow" variant="light">
                                      ⭐ {t.rating}/5
                                    </Badge>
                                  )}
                                </Stack>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {t.callStartTime 
                                    ? new Date(t.callStartTime).toLocaleString()
                                    : t.createdAt 
                                      ? new Date(t.createdAt).toLocaleString()
                                      : '-'}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                {t.rating ? (
                                  <Badge color="yellow" variant="light">
                                    ⭐ {t.rating}/5
                                  </Badge>
                                ) : (
                                  <Text size="xs" c="dimmed">-</Text>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>

                      {/* Pagination */}
                      <Group justify="center" mt="md">
                        <Pagination
                          value={transactionsPage}
                          onChange={setTransactionsPage}
                          total={Math.ceil(transactionsTotal / 10)}
                          color="blue"
                        />
                      </Group>
                    </>
                  )}
                </Paper>
              </Stack>
            )}
            
            {/* Queue Tab - Manager/Supervisor Only */}
            {(propRole === 'manager' || propRole === 'supervisor') && activeTab === 'queue' && (
              <Stack gap="md">
                <Group justify="space-between" align="center">
                  <Group gap="sm">
                    <Avatar size={40} radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'cyan' }}>
                      <IconMessage size={22} />
                    </Avatar>
                    <div>
                      <Text fw={700} size="lg" c="#2d2d2d">📞 Chat Queue</Text>
                      <Text size="xs" c="dimmed">Manage escalated customer conversations (1-by-1)</Text>
                    </div>
                  </Group>
                  <Group gap="xs">
                    <Badge size="lg" color="orange" variant="dot">
                      {transferredChats.length} Pending
                    </Badge>
                    <Badge size="lg" color="green" variant="dot">
                      {acceptedChats.length} Active
                    </Badge>
                  </Group>
                </Group>

                <SimpleGrid cols={2} spacing="md">
                  {/* Pending Queue Section */}
                  <Paper p="md" radius="lg" style={{ border: '1px solid #e8e5f0', background: '#fafbff' }}>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Text fw={700} size="lg" c="#2d2d2d">⏳ Pending Queue</Text>
                        <Badge color="orange" size="lg">{transferredChats.length + dedupedEscalatedChats.length}</Badge>
                      </Group>

                      {transferredChats.length === 0 && dedupedEscalatedChats.length === 0 ? (
                        <Box ta="center" py="xl" c="dimmed">
                          <Text size="sm">No pending chats - all caught up! 🎉</Text>
                        </Box>
                      ) : (
                        <Stack gap="sm">
                          {/* Transferred Chats */}
                          {transferredChats.map((chat, idx) => (
                            <Paper
                              key={chat.roomId}
                              p="sm"
                              radius="md"
                              style={{
                                background: '#ffffff',
                                border: '1px solid #e8e5f0',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: '#9381ff', boxShadow: '0 2px 8px rgba(147, 129, 255, 0.1)' }
                              }}
                              onClick={() => acceptTransferredChat(chat)}
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Box style={{ flex: 1 }}>
                                  <Group gap="xs" mb="xs">
                                    <Text fw={600} size="sm" c="#2d2d2d">{idx + 1}. {chat.clientName}</Text>
                                    <Badge size="xs" color="orange" variant="light">Escalated</Badge>
                                  </Group>
                                  <Text size="xs" c="dimmed">From: {chat.fromAgentName}</Text>
                                  <Text size="xs" c="dimmed" mt={2}>{chat.message}</Text>
                                  <Text size="xs" c="dimmed" mt="xs">
                                    Waiting since: {new Date(chat.timestamp).toLocaleTimeString()}
                                  </Text>
                                </Box>
                                <Button
                                  size="xs"
                                  color="blue"
                                  variant="light"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    acceptTransferredChat(chat);
                                  }}
                                >
                                  Accept
                                </Button>
                              </Group>
                            </Paper>
                          ))}
                          
                          {/* Escalated Active Chats from Database (deduplicated) */}
                          {dedupedEscalatedChats.map((chat, idx) => (
                            <Paper
                              key={chat.roomId}
                              p="sm"
                              radius="md"
                              style={{
                                background: '#ffffff',
                                border: '1px solid #fed7aa',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': { borderColor: '#f97316', boxShadow: '0 2px 8px rgba(249, 115, 22, 0.1)' }
                              }}
                              onClick={() => acceptTransferredChat(chat)}
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Box style={{ flex: 1 }}>
                                  <Group gap="xs" mb="xs">
                                    <Text fw={600} size="sm" c="#2d2d2d">{transferredChats.length + idx + 1}. {chat.clientName}</Text>
                                    <Badge size="xs" color="orange" variant="filled">Escalated</Badge>
                                  </Group>
                                  <Text size="xs" c="dimmed">From: {chat.fromAgentName}</Text>
                                  {chat.subject && <Text size="xs" c="dimmed" mt={2}>Subject: {chat.subject}</Text>}
                                  {chat.notes && <Text size="xs" c="dimmed" mt={2}>Notes: {chat.notes.substring(0, 50)}{chat.notes.length > 50 ? '...' : ''}</Text>}
                                  <Text size="xs" c="dimmed" mt="xs">
                                    Escalated: {new Date(chat.escalatedAt).toLocaleTimeString()}
                                  </Text>
                                </Box>
                                <Button
                                  size="xs"
                                  color="orange"
                                  variant="light"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    acceptTransferredChat(chat);
                                  }}
                                >
                                  Accept
                                </Button>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>

                  {/* Active Chats Section */}
                  <Paper p="md" radius="lg" style={{ border: '1px solid #e8e5f0', background: '#f0fdf4' }}>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Text fw={700} size="lg" c="#2d2d2d">✅ Active Chats</Text>
                        <Badge color="green" size="lg">{acceptedChats.length}</Badge>
                      </Group>

                      {acceptedChats.length === 0 ? (
                        <Box ta="center" py="xl" c="dimmed">
                          <Text size="sm">No active chats - waiting for escalations</Text>
                        </Box>
                      ) : (
                        <Stack gap="sm">
                          {acceptedChats.map((chat, idx) => (
                            <Paper
                              key={chat.roomId}
                              p="sm"
                              radius="md"
                              style={{
                                background: selectedChat?.roomId === chat.roomId ? '#ede9ff' : '#ffffff',
                                border: selectedChat?.roomId === chat.roomId ? '2px solid #9381ff' : '1px solid #e8e5f0',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => acceptTransferredChat(chat)}
                            >
                              <Group justify="space-between" wrap="nowrap">
                                <Box style={{ flex: 1 }}>
                                  <Group gap="xs" mb="xs">
                                    <Text fw={600} size="sm" c="#2d2d2d">
                                      {idx + 1}. {chat.clientName}
                                    </Text>
                                    {selectedChat?.roomId === chat.roomId && (
                                      <Badge size="xs" color="blue" variant="filled">Active</Badge>
                                    )}
                                  </Group>
                                  <Text size="xs" c="dimmed">From: {chat.fromAgentName}</Text>
                                  <Text size="xs" c="dimmed" mt={2}>{chatMessages.length} messages</Text>
                                  <Text size="xs" c="dimmed" mt="xs">
                                    Accepted: {new Date(chat.acceptedAt).toLocaleTimeString()}
                                  </Text>
                                </Box>
                                <Group gap={6}>
                                  <Tooltip label="Open chat">
                                    <ActionIcon
                                      size="sm"
                                      color="blue"
                                      variant="light"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        acceptTransferredChat(chat);
                                      }}
                                    >
                                      <IconMessage size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Resolve chat">
                                    <ActionIcon
                                      size="sm"
                                      color="green"
                                      variant="light"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        resolveChat(chat.roomId);
                                      }}
                                    >
                                      <IconCheck size={14} />
                                    </ActionIcon>
                                  </Tooltip>
                                </Group>
                              </Group>
                            </Paper>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                </SimpleGrid>

                {(transferredChats.length > 0 || acceptedChats.length > 0) && (
                  <Paper p="md" radius="lg" style={{ background: '#fef3c7', border: '1px solid #fcd34d' }}>
                    <Group gap="sm">
                      <Text fw={600} c="#92400e">💡 Tip:</Text>
                      <Text size="sm" c="#78350f">
                        You can only handle one active chat at a time. Accept escalated customers from the pending queue and complete chats when done to handle the next customer.
                      </Text>
                    </Group>
                  </Paper>
                )}

                {/* Escalated Customers Monitoring Table */}
                {(() => {
                  const allEscalated = [
                    ...transferredChats.map(c => ({ ...c, _status: 'pending' })),
                    ...acceptedChats.map(c => ({ ...c, _status: 'accepted' })),
                    ...escalationHistory.map(c => ({ ...c, _status: 'completed' })),
                  ];
                  return (
                    <Paper p="md" radius="lg" style={{ border: '1px solid #e8e5f0', background: '#fff' }}>
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Text fw={700} size="lg" c="#2d2d2d">🔍 Escalated Customers</Text>
                          <Group gap="xs">
                            <Badge color="orange" size="lg" variant="light">{transferredChats.length} Pending</Badge>
                            <Badge color="green" size="lg" variant="light">{acceptedChats.length} Active</Badge>
                            <Badge color="gray" size="lg" variant="light">{escalationHistory.length} Completed</Badge>
                          </Group>
                        </Group>
                        {allEscalated.length === 0 ? (
                          <Box ta="center" py="lg" c="dimmed">
                            <Text size="sm">No escalated customers at the moment</Text>
                          </Box>
                        ) : (
                          <ScrollArea>
                            <Table striped highlightOnHover withTableBorder withColumnBorders>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>#</Table.Th>
                                  <Table.Th>Customer</Table.Th>
                                  <Table.Th>Email</Table.Th>
                                  <Table.Th>Subject</Table.Th>
                                  <Table.Th>Escalated By</Table.Th>
                                  <Table.Th>Notes / Summary</Table.Th>
                                  <Table.Th>Time</Table.Th>
                                  <Table.Th>Status</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {allEscalated.map((chat, idx) => (
                                  <Table.Tr
                                    key={chat.roomId + '-' + chat._status}
                                    style={{ cursor: chat._status !== 'completed' ? 'pointer' : 'default', background: selectedChat?.roomId === chat.roomId ? '#ede9ff' : undefined }}
                                    onClick={() => { if (chat._status !== 'completed') acceptTransferredChat(chat); }}
                                  >
                                    <Table.Td>{idx + 1}</Table.Td>
                                    <Table.Td>
                                      <Group gap="xs">
                                        <Avatar size={24} radius="xl" color={avatarColor(chat.clientName)}>
                                          {getInitial(chat.clientName)}
                                        </Avatar>
                                        <Text size="sm" fw={500}>{chat.clientName}</Text>
                                      </Group>
                                    </Table.Td>
                                    <Table.Td><Text size="xs" c="dimmed">{chat.clientEmail || '-'}</Text></Table.Td>
                                    <Table.Td><Text size="xs" lineClamp={1}>{chat.subject || '-'}</Text></Table.Td>
                                    <Table.Td>
                                      <Badge size="xs" color="blue" variant="light">{chat.fromAgentName}</Badge>
                                    </Table.Td>
                                    <Table.Td
                                      style={{ maxWidth: 200, cursor: chat.notes ? 'pointer' : 'default' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (chat.notes) {
                                          setEscalationNotesModal({ clientName: chat.clientName, notes: chat.notes, fromAgentName: chat.fromAgentName });
                                        }
                                      }}
                                    >
                                      {chat.notes ? (
                                        <Tooltip label="Click to view full summary" withArrow>
                                          <Text size="xs" c="blue" td="underline" lineClamp={1}>{chat.notes}</Text>
                                        </Tooltip>
                                      ) : (
                                        <Text size="xs" c="dimmed">-</Text>
                                      )}
                                    </Table.Td>
                                    <Table.Td><Text size="xs" c="dimmed">{new Date(chat.timestamp).toLocaleTimeString()}</Text></Table.Td>
                                    <Table.Td>
                                      <Badge size="xs" color={chat._status === 'accepted' ? 'green' : chat._status === 'completed' ? 'gray' : 'orange'} variant="light">
                                        {chat._status === 'accepted' ? 'Active' : chat._status === 'completed' ? 'Completed' : 'Pending'}
                                      </Badge>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>
                          </ScrollArea>
                        )}
                      </Stack>
                    </Paper>
                  );
                })()}
              </Stack>
            )}
            
            {/* Roles Tab */}
            {activeTab === 'roles' && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700} size="lg" c="#2d2d2d">Role Management</Text>
                  <Button leftSection={<IconPlus size={16} />} onClick={() => {
                    setRoleForm({ id: '', name: '', description: '', color: 'blue', permissions: [] });
                    modals.openModal('role', { mode: 'create' });
                  }}>
                    Add Role
                  </Button>
                </Group>
                
                <SimpleGrid cols={2}>
                  {roles.map((role) => (
                    <Paper key={role.id} className={classes.statCard} p="md">
                      <Stack gap="sm">
                        <Group justify="space-between">
                          <Group gap="sm">
                            <Avatar color={role.color} radius="xl" size={40}>
                              <IconShield size={20} />
                            </Avatar>
                            <div>
                              <Text fw={700} c="#2d2d2d">{role.name}</Text>
                              <Text size="xs" c="dimmed">{role.description}</Text>
                            </div>
                          </Group>
                          <Group gap={4}>
                            <Tooltip label="Edit">
                              <ActionIcon variant="subtle" color="blue" onClick={() => {
                                setRoleForm({ ...role });
                                modals.openModal('role', { mode: 'edit' });
                              }}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                            {role.id !== 'super_admin' && (
                              <Tooltip label="Delete">
                                <ActionIcon variant="subtle" color="red" onClick={() => handleDeleteRole(role.id)}>
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Group>
                        <Group gap="xs">
                          <Badge size="sm" variant="light" color={role.color}>
                            {role.id}
                          </Badge>
                        </Group>
                        {/* Permissions Display */}
                        <Stack gap={4} mt="xs">
                          <Text size="xs" fw={600} c="dimmed">Permissions:</Text>
                          <SimpleGrid cols={2}>
                            {permissions.filter(p => p.active).map(perm => (
                              <Group key={perm.id} gap={4}>
                                <Box w={6} h={6} bg={role.permissions?.includes(perm.id) ? 'green' : 'gray'} radius="xs" />
                                <Text size="xs" c={role.permissions?.includes(perm.id) ? 'white' : 'dimmed'}>{perm.name}</Text>
                              </Group>
                            ))}
                          </SimpleGrid>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </SimpleGrid>
              </Stack>
            )}
            
            {/* Permissions Tab */}
            {activeTab === 'permissions' && (
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700} size="lg" c="#2d2d2d">Permission Management</Text>
                  <Button leftSection={<IconPlus size={16} />} onClick={() => {
                    setPermissionForm({ id: '', name: '', description: '', active: true });
                    modals.openModal('permission', { mode: 'create' });
                  }}>
                    Add Permission
                  </Button>
                </Group>
                
                <Text size="sm" c="dimmed">
                  Manage permissions that can be assigned to roles. Inactive permissions will not appear in role assignment.
                </Text>
                
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th c="#2d2d2d">Permission ID</Table.Th>
                      <Table.Th c="#2d2d2d">Name</Table.Th>
                      <Table.Th c="#2d2d2d">Description</Table.Th>
                      <Table.Th c="#2d2d2d">Active</Table.Th>
                      <Table.Th c="#2d2d2d">Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {permissions.map((perm) => (
                      <Table.Tr key={perm._id}>
                        <Table.Td>
                          <Text size="sm" c="#2d2d2d">{perm.id}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="#2d2d2d">{perm.name}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{perm.description}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Switch
                            checked={perm.active}
                            onChange={() => togglePermissionActive(perm.id)}
                            color="green"
                            size="sm"
                          />
                        </Table.Td>
                        <Table.Td>
                          <Group gap={4}>
                            <Tooltip label="Edit">
                              <ActionIcon variant="subtle" color="blue" onClick={() => {
                                setPermissionForm({ ...perm });
                                modals.openModal('permission', { mode: 'edit' });
                              }}>
                                <IconEdit size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon variant="subtle" color="red" onClick={() => handleDeletePermission(perm.id)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Stack>
            )}
          </Grid.Col>
        </Grid>
      </Box>

      {/* User Modal */}
      <UserModal
        open={isUserModalOpen}
        onClose={() => modals.closeModal('user')}
        mode={userModalState.data?.mode || 'create'}
        data={userForm}
        onChange={handleUserFieldChange}
        onSubmit={handleUserSubmit}
        roles={getRoleOptions()}
      />

      {/* Client Modal */}
      <ClientModal
        open={isClientModalOpen}
        onClose={() => modals.closeModal('client')}
        mode={clientModalState.data?.mode || 'create'}
        data={clientForm}
        onChange={handleClientFieldChange}
        onSubmit={handleClientSubmit}
      />
      {/* Role Modal */}
      <Modal
        opened={isRoleModalOpen}
        onClose={() => modals.closeModal('role')}
        title={roleModalState.data?.mode === 'create' ? 'Create New Role' : 'Edit Role'}
        centered
        size="md"
      >
        <Stack gap="sm">
          <Select
            label="Role ID"
            value={roleForm.id}
            onChange={(value) => {
              const selectedRole = roles.find(r => r.id === value);
              const autoGenerateName = (id) => {
                if (!id) return '';
                const nameMap = {
                  'helpdesk_agent': 'Help Desk Agent',
                  'supervisor': 'Supervisor',
                  'manager': 'Manager',
                  'super_admin': 'Super Admin'
                };
                if (nameMap[id]) return nameMap[id];
                return id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              };
              setRoleForm({ 
                ...roleForm, 
                id: value || '',
                name: selectedRole?.name || autoGenerateName(value) || '',
                description: selectedRole?.description || '',
                color: selectedRole?.color || 'blue',
                permissions: selectedRole?.permissions || []
              });
            }}
            placeholder="Select a role ID"
            data={[
              { value: 'helpdesk_agent', label: 'helpdesk_agent - Help Desk Agent' },
              { value: 'supervisor', label: 'supervisor - Supervisor' },
              { value: 'manager', label: 'manager - Manager' },
              { value: 'super_admin', label: 'super_admin - Super Admin' },
              ...roles
                .filter(r => !['helpdesk_agent', 'supervisor', 'manager', 'super_admin'].includes(r.id))
                .map(r => ({ value: r.id, label: `${r.id} - ${r.name}` }))
            ]}
            searchable
            disabled={roleModalState.data?.mode === 'edit'}
            required
          />
          <TextInput
            label="Role Name"
            value={roleForm.name}
            onChange={(e) => handleRoleFieldChange('name', e.target.value)}
            placeholder="e.g., Help Desk Agent"
            required
          />
          <TextInput
            label="Description"
            value={roleForm.description}
            onChange={(e) => handleRoleFieldChange('description', e.target.value)}
            placeholder="Brief description"
          />
          <Select
            label="Color"
            value={roleForm.color}
            onChange={(value) => handleRoleFieldChange('color', value)}
            data={[
              { value: 'blue', label: 'Blue' },
              { value: 'green', label: 'Green' },
              { value: 'red', label: 'Red' },
              { value: 'orange', label: 'Orange' },
              { value: 'violet', label: 'Violet' },
              { value: 'cyan', label: 'Cyan' },
              { value: 'pink', label: 'Pink' },
              { value: 'gray', label: 'Gray' }
            ]}
          />
          <Divider label="Permissions" labelPosition="center" my="sm" />
          <Stack gap="xs">
            {permissions.filter(p => p.active).map(perm => (
              <Checkbox
                key={perm._id}
                label={perm.name}
                checked={roleForm.permissions?.includes(perm.id) || false}
                onChange={(e) => {
                  const newPermissions = e.target.checked
                    ? [...(roleForm.permissions || []), perm.id]
                    : (roleForm.permissions || []).filter(p => p !== perm.id);
                  handleRoleFieldChange('permissions', newPermissions);
                }}
              />
            ))}
          </Stack>
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => modals.closeModal('role')}>Cancel</Button>
            <Button onClick={handleRoleSubmit}>
              {roleModalState.data?.mode === 'create' ? 'Create Role' : 'Save Changes'}
            </Button>
          </Group>
        </Stack>
      </Modal>
      
      {/* Permission Modal */}
      <Modal
        opened={isPermissionModalOpen}
        onClose={() => modals.closeModal('permission')}
        title={permissionModalState.data?.mode === 'create' ? 'Create New Permission' : 'Edit Permission'}
        centered
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Permission ID"
            value={permissionForm.id}
            onChange={(e) => handlePermissionFieldChange('id', e.target.value)}
            placeholder="e.g., canManageUsers"
            disabled={permissionModalState.data?.mode === 'edit'}
            required
          />
          <TextInput
            label="Permission Name"
            value={permissionForm.name}
            onChange={(e) => handlePermissionFieldChange('name', e.target.value)}
            placeholder="e.g., Manage Users"
            required
          />
          <TextInput
            label="Description"
            value={permissionForm.description}
            onChange={(e) => handlePermissionFieldChange('description', e.target.value)}
            placeholder="Brief description"
          />
          <Checkbox
            label="Active"
            checked={permissionForm.active}
            onChange={(e) => handlePermissionFieldChange('active', e.target.checked)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={() => modals.closeModal('permission')}>Cancel</Button>
            <Button onClick={handlePermissionSubmit}>
              {permissionModalState.data?.mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Profile Drawer ── */}
      <Drawer opened={profileOpen} onClose={() => setProfileOpen(false)} position="right" size={380} withCloseButton
        title={<Text fw={700} c="#2d2d2d">My Profile</Text>}
        styles={{
          content: { background: '#ffffff', borderLeft: '1px solid #e8e5f0' },
          header: { background: '#ffffff', borderBottom: '1px solid #e8e5f0' },
          close: { color: '#9895b5' },
        }}>
        <ScrollArea h="calc(100vh - 70px)" scrollbarSize={4}>
          <Stack gap="md" p="md">

            {/* Hero section */}
            <Paper p="xl" radius="lg" style={{ background: '#f5f3ff', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
              <Box style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: '#f5f3ff' }} />
              <Box style={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(124,92,191,0.1)' }} />
              <Indicator size={14} color={myStatus === 'online' ? 'green' : 'orange'} offset={6} position="bottom-end" withBorder style={{ display: 'inline-flex' }}>
                <Avatar size={80} variant="gradient" gradient={roleConfig.gradient} radius={80}
                  style={{ margin: '0 auto', fontSize: '2rem', fontWeight: 800, boxShadow: '0 0 0 4px #e8e5f0' }}>
                  {(currentUser?.fullName || 'SA').charAt(0).toUpperCase()}
                </Avatar>
              </Indicator>
              <Text fw={800} c="#2d2d2d" size="lg" mt="sm">{currentUser?.fullName || 'Super Admin'}</Text>
              <Badge variant="light" color="violet" mt={4}>
                {currentUser?.role === 'super_admin' ? 'Super Admin' : currentUser?.role === 'manager' ? 'Manager' : currentUser?.role === 'supervisor' ? 'Supervisor' : 'Agent'}
              </Badge>
              <Group justify="center" gap="xs" mt="sm">
                <Badge color={myStatus === 'online' ? 'green' : 'orange'} variant="dot" size="sm" style={{ cursor: 'pointer' }} onClick={handleToggleMyStatus}>
                  {myStatus === 'online' ? '● Online' : '● Away'}
                </Badge>
              </Group>
            </Paper>

            {/* Contact info */}
            <Paper p="md" radius="md" style={{ background: '#f9f8fc', border: '1px solid #e8e5f0' }}>
              <Text size="sm" fw={600} c="#2d2d2d" mb="sm">Contact Information</Text>
              <Stack gap="xs">
                {[
                  ['✉', 'Email', currentUser?.email || 'Not set'],
                  ['📞', 'Phone', currentUser?.phone || 'Not set'],
                  ['🏢', 'Department', currentUser?.department || 'Not set'],
                  ['👤', 'Username', currentUser?.username || 'Not set'],
                  ['📅', 'Joined', currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'],
                  ['🕐', 'Last Login', currentUser?.lastLogin ? new Date(currentUser.lastLogin).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'],
                ].map(([icon, label, value]) => (
                  <Group key={label} gap="sm" wrap="nowrap">
                    <Text size="sm">{icon}</Text>
                    <Box>
                      <Text size="xs" c="dimmed">{label}</Text>
                      <Text size="xs" c="#2d2d2d" fw={500}>{value}</Text>
                    </Box>
                  </Group>
                ))}
              </Stack>
            </Paper>

            {/* Role & Permissions */}
            <Paper p="md" radius="md" style={{ background: '#f9f8fc', border: '1px solid #e8e5f0' }}>
              <Text size="sm" fw={600} c="#2d2d2d" mb="sm">Role & Permissions</Text>
              <Badge color="violet" variant="light" size="lg" mb="sm">
                {currentUser?.role === 'super_admin' ? 'Super Admin' : currentUser?.role === 'manager' ? 'Manager' : currentUser?.role === 'supervisor' ? 'Supervisor' : 'Agent'}
              </Badge>
              <Stack gap={4} mt="xs">
                {[
                  ['Manage Users', currentUser?.role === 'super_admin' || currentUser?.role === 'manager'],
                  ['Manage Clients', currentUser?.role === 'super_admin'],
                  ['View Reports', currentUser?.role !== 'helpdesk_agent'],
                  ['Manage Settings', currentUser?.role === 'super_admin'],
                  ['Handle Chats', true],
                ].map(([perm, enabled]) => (
                  <Group key={perm} gap={6}>
                    <Box w={8} h={8} style={{ borderRadius: '50%', background: enabled ? '#40c057' : '#495057' }} />
                    <Text size="xs" c={enabled ? 'white' : 'dimmed'}>{perm}</Text>
                  </Group>
                ))}
              </Stack>
            </Paper>

            {/* Actions */}
            <Stack gap="xs">
              <Button variant="light" color={myStatus === 'online' ? 'orange' : 'green'} radius="xl" fullWidth onClick={handleToggleMyStatus}>
                {myStatus === 'online' ? '🌙 Set Away' : '🟢 Set Online'}
              </Button>
              <Button variant="light" color="blue" radius="xl" fullWidth onClick={handleOpenEditProfile}>✏️ Edit Profile</Button>
              <Button variant="light" color="red" radius="xl" fullWidth onClick={() => { setProfileOpen(false); handleLogout(); }}>🚪 Logout</Button>
            </Stack>
          </Stack>
        </ScrollArea>
      </Drawer>

      {/* ── Voucher Diagnostic Drawer ── */}
      <Drawer 
        opened={voucherSidebarOpen} 
        onClose={() => setVoucherSidebarOpen(false)} 
        position="right" 
        size={350}
        withCloseButton={false}
        styles={{
          content: { background: '#ffffff' },
          body: { padding: 0, height: '100%' }
        }}
      >
        <VoucherDiagnosticSidebar 
          email={voucherClientEmail} 
          onClose={() => setVoucherSidebarOpen(false)}
        />
      </Drawer>

      {/* ── Edit Profile Modal ── */}
      <Modal
        opened={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        title="Edit Profile"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Full Name"
            value={editProfileForm.fullName}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, fullName: e.target.value })}
            placeholder="Enter your full name"
          />
          <TextInput
            label="Phone"
            value={editProfileForm.phone}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, phone: e.target.value })}
            placeholder="Enter phone number"
          />
          <TextInput
            label="Department"
            value={editProfileForm.department}
            onChange={(e) => setEditProfileForm({ ...editProfileForm, department: e.target.value })}
            placeholder="Enter department"
          />
          <Group justify="flex-end" gap="sm" mt="md">
            <Button variant="subtle" onClick={() => setEditProfileOpen(false)}>
              Cancel
            </Button>
            <Button color="blue" onClick={handleEditProfile}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Queue Modal - Shows list of transferred chats */}
      <Modal
        opened={isQueueModalOpen}
        onClose={() => modals.closeModal('queue')}
        title="Transfer Queue"
        centered
        size="lg"
      >
        <Stack gap="sm">
          {transferredChats.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No transferred chats in queue
            </Text>
          ) : (
            <ScrollArea h={400}>
              <Stack gap="xs">
                {transferredChats.map((chat, index) => (
                  <Paper key={index} p="sm" withBorder>
                    <Group justify="space-between">
                      <Stack gap={4}>
                        <Text fw={600}>{chat.clientName}</Text>
                        <Text size="sm" c="dimmed">From: {chat.fromAgentName}</Text>
                        <Text size="xs" c="dimmed">
                          {new Date(chat.timestamp).toLocaleString()}
                        </Text>
                      </Stack>
                      <Group>
                        <Badge color={chat.status === 'pending' ? 'orange' : 'green'}>
                          {chat.status}
                        </Badge>
                        <Button
                          size="xs"
                          color="blue"
                          onClick={() => acceptTransferredChat(chat)}
                        >
                          Accept
                        </Button>
                      </Group>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </ScrollArea>
          )}
        </Stack>
      </Modal>

      {/* ── Escalation Notes/Summary Modal ── */}
      <Modal
        opened={!!escalationNotesModal}
        onClose={() => setEscalationNotesModal(null)}
        title={`Escalation Summary — ${escalationNotesModal?.clientName || ''}`}
        centered
        size="md"
      >
        {escalationNotesModal && (
          <Stack gap="md">
            <Group gap="xs">
              <Text size="sm" fw={600} c="dimmed">Escalated by:</Text>
              <Badge color="blue" variant="light">{escalationNotesModal.fromAgentName}</Badge>
            </Group>
            <Divider />
            <Text size="sm" fw={600}>Summary / Notes:</Text>
            <Paper p="md" radius="md" style={{ background: '#f8f9fa', border: '1px solid #e9ecef', whiteSpace: 'pre-wrap' }}>
              <Text size="sm">{escalationNotesModal.notes}</Text>
            </Paper>
            <Group justify="flex-end">
              <Button variant="light" color="gray" onClick={() => setEscalationNotesModal(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Floating Chat Interface Modal ── */}
      <Modal
        opened={isChatModalOpen}
        onClose={() => { modals.closeModal('chat'); setSelectedChat(null); setChatMessages([]); setShowEmoji(false); }}
        withCloseButton={false}
        centered
        size="lg"
        padding={0}
        radius="lg"
        styles={{
          content: { background: '#ffffff', border: '1px solid #e8e5f0', overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '85vh' },
          body: { padding: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }
        }}
      >
        {/* Chat Header */}
        <Box style={{ background: '#ffffff', borderBottom: '1px solid #e8e5f0', padding: '12px 16px' }}>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap">
              <Indicator position="bottom-end" size={11} color="green">
                <Avatar color={avatarColor(selectedChat?.clientName)} radius="xl" size={42}>
                  {getInitial(selectedChat?.clientName)}
                </Avatar>
              </Indicator>
              <Box>
                <Group gap="xs">
                  <Text fw={700} c="#2d2d2d" size="sm">{selectedChat?.clientName || 'Customer'}</Text>
                  <Badge size="xs" color="orange" variant="light">Escalated</Badge>
                </Group>
                <Text size="xs" c="dimmed">From: {selectedChat?.fromAgentName || 'Agent'}</Text>
              </Box>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <Tooltip label="Voice call">
                <ActionIcon variant="light" color="blue" radius="xl" onClick={() => initiateCall('audio')}>
                  <IconPhone size={15}/>
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Video call">
                <ActionIcon variant="light" color="violet" radius="xl" onClick={() => initiateCall('video')}>
                  <IconVideo size={15}/>
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Close chat">
                <ActionIcon variant="light" color="gray" radius="xl" onClick={() => { modals.closeModal('chat'); setSelectedChat(null); setChatMessages([]); }}>
                  <IconX size={15}/>
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Box>

        {/* Chat Messages Area */}
        <ScrollArea style={{ flex: 1, minHeight: 300, maxHeight: '55vh' }} px="lg" py="md" scrollbarSize={4}>
          <Stack gap="xs" px="md" py="sm">
            {chatMessages.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" mt="xl">No messages yet — loading chat history…</Text>
            )}
            {chatMessages.map((msg, i) => {
              const isUser = msg.sender === 'user';
              const isSystem = msg.sender === 'system' || msg.isCallEnded;
              const showTime = i === 0 || Math.abs(new Date(msg.timestamp) - new Date(chatMessages[i - 1]?.timestamp)) > 300000;

              if (isSystem) {
                return (
                  <Box key={i}>
                    {showTime && <Text size="xs" c="dimmed" ta="center" my={4}>{formatTime(msg.timestamp)}</Text>}
                    <Paper radius="md" px="md" py="sm" maw="80%" mx="auto"
                      style={{ background: '#ede9ff', border: '1px solid #e8e5f0', borderRadius: '14px', textAlign: 'center' }}>
                      <Text size="sm" c="#9381ff" fw={500}>{msg.text}</Text>
                    </Paper>
                  </Box>
                );
              }

              return (
                <Box key={i}>
                  {showTime && <Text size="xs" c="dimmed" ta="center" my="xs">{formatTime(msg.timestamp)}</Text>}
                  <Flex justify={isUser ? 'flex-start' : 'flex-end'} align="flex-end" gap="xs">
                    {isUser && <Avatar size={30} color={avatarColor(selectedChat?.clientName)} radius="xl">{getInitial(selectedChat?.clientName)}</Avatar>}
                    <Paper radius="md" px="md" py="sm" maw="58%"
                      style={{
                        background: isUser ? '#ede9ff' : 'linear-gradient(135deg,#9381ff,#b8b8ff)',
                        borderRadius: isUser ? '14px 14px 14px 4px' : '14px 14px 4px 14px'
                      }}>
                      <Text size="xs" c={isUser ? 'dimmed' : 'rgba(255,255,255,0.7)'} fw={600} mb={2}>
                        {isUser ? (selectedChat?.clientName || 'Customer') : 'Manager'}
                      </Text>
                      <Text size="sm" c="#2d2d2d">{msg.text}</Text>
                      <Text size="xs" c={isUser ? 'dimmed' : 'rgba(255,255,255,0.5)'} ta="right" mt={3}>
                        {formatTime(msg.timestamp)}
                      </Text>
                    </Paper>
                    {!isUser && (
                      <Avatar size={30} variant="gradient" gradient={{ from: 'blue', to: 'violet' }} radius="xl">{getInitial(currentUser?.fullName)}</Avatar>
                    )}
                  </Flex>
                </Box>
              );
            })}
            <div ref={chatMessagesEnd}/>
          </Stack>
        </ScrollArea>

        {/* Input Bar */}
        <Box style={{ borderTop: '1px solid #e8e5f0', padding: '12px 16px', background: '#ffffff', boxShadow: '0 -1px 4px rgba(0, 0, 0, 0.03)' }}>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              flex={1}
              placeholder="Say something..."
              value={chatReply}
              onChange={e => setChatReply(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatReply(); } }}
              styles={{ input: { background: 'transparent', border: 'none', borderRadius: 0, color: '#2d2d2d', '&::placeholder': { color: '#6b6894' } } }}
            />
            <Group gap={3} style={{ flexShrink: 0 }}>
              <Box style={{ position: 'relative' }}>
                <Tooltip label="Emoji">
                  <ActionIcon variant="subtle" color="gray" onClick={() => setShowEmoji(o => !o)}>
                    <IconMoodSmile size={17}/>
                  </ActionIcon>
                </Tooltip>
                {showEmoji && (
                  <Paper shadow="xl" p="xs" style={{ position: 'absolute', bottom: 40, right: 0, zIndex: 200, background: '#ffffff', border: '1px solid #e8e5f0', width: 196 }}>
                    <Group gap={3}>
                      {EMOJIS.map(e => (
                        <UnstyledButton key={e} onClick={() => setChatReply(prev => prev + e)} style={{ fontSize: 20, padding: 4, borderRadius: 6 }}>
                          {e}
                        </UnstyledButton>
                      ))}
                    </Group>
                  </Paper>
                )}
              </Box>
              <Tooltip label="Attach">
                <ActionIcon variant="subtle" color="gray"><IconPaperclip size={17}/></ActionIcon>
              </Tooltip>
              <Tooltip label="Image">
                <ActionIcon variant="subtle" color="gray"><IconPhoto size={17}/></ActionIcon>
              </Tooltip>
              <Tooltip label="Audio">
                <ActionIcon variant="subtle" color="gray"><IconMicrophone size={17}/></ActionIcon>
              </Tooltip>
              <ActionIcon size="lg" radius="xl" variant="gradient" gradient={{ from: 'blue', to: 'violet' }} onClick={sendChatReply}>
                <IconSend size={15}/>
              </ActionIcon>
            </Group>
          </Group>
        </Box>
      </Modal>

      {/* ── Incoming Call Modal ── */}
      {incomingCall && !callStatus && (
        <Modal opened onClose={rejectCall} withCloseButton={false} centered closeOnClickOutside={false}
          styles={{ content: { background: '#1a2035', border: '1px solid #2a3a5e' } }}>
          <Stack align="center" gap="md" py="lg">
            <Box style={{
              width: 100, height: 100, borderRadius: '50%',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 12px rgba(245,87,108,0.15)', animation: 'pulse 2s infinite'
            }}>
              {incomingCall.callType === 'video' ? <IconVideo size={48} color="white"/> : <IconPhone size={48} color="white"/>}
            </Box>
            <Title order={4} c="#2d2d2d" fw={700}>Incoming {incomingCall.callType === 'video' ? 'Video' : 'Audio'} Call</Title>
            <Text c="dimmed" size="sm" ta="center">{incomingCall.from || 'Customer'} is calling you</Text>
            <Group gap="xl" mt="md">
              <ActionIcon size={60} radius="xl" color="red" variant="filled" onClick={rejectCall}
                style={{ boxShadow: '0 4px 20px rgba(255,0,0,0.4)' }}>
                <IconPhoneOff size={28}/>
              </ActionIcon>
              <ActionIcon size={60} radius="xl" color="green" variant="filled" onClick={acceptCall}
                style={{ boxShadow: '0 4px 20px rgba(0,255,0,0.4)' }}>
                <IconPhone size={28}/>
              </ActionIcon>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* ── Outgoing / Ringing Call Modal ── */}
      {!incomingCall && callStatus === 'calling' && isCallModalOpen && (
        <Modal opened onClose={() => { endCall(); toast('Call cancelled', 'red'); }} withCloseButton={false} centered closeOnClickOutside={false}
          styles={{ content: { background: '#1a2035', border: '1px solid #2a3a5e' } }}>
          <Stack align="center" gap="md" py="lg">
            <Box style={{
              width: 100, height: 100, borderRadius: '50%',
              background: 'linear-gradient(135deg, #9381ff 0%, #b8b8ff 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 0 12px #e8e5f0', animation: 'pulse 2s infinite'
            }}>
              {callType === 'video' ? <IconVideo size={48} color="white"/> : <IconPhone size={48} color="white"/>}
            </Box>
            <Title order={4} c="#2d2d2d" fw={700}>Calling {selectedChat?.clientName || 'Customer'}...</Title>
            <Text c="dimmed" size="sm">{callType === 'video' ? 'Video' : 'Voice'} call · Ringing</Text>
            <Group gap="xl" mt="md">
              <ActionIcon size={60} radius="xl" color="red" variant="filled"
                onClick={() => { endCall(); toast('Call cancelled', 'red'); }}
                style={{ boxShadow: '0 4px 20px rgba(255,0,0,0.4)' }}>
                <IconPhoneOff size={28}/>
              </ActionIcon>
            </Group>
          </Stack>
        </Modal>
      )}

      {/* ── Active Call Modal ── */}
      {callStatus === 'connected' && callType && (
        <Modal opened onClose={endCall} withCloseButton={false} centered size="lg" closeOnClickOutside={false}
          styles={{ content: { background: '#1a2035', border: '1px solid #2a3a5e' }, body: { padding: 0 } }}>
          <Stack align="center" gap="md" py="lg" px="md">
            <audio ref={audioRef => { if (audioRef && remoteStream) { audioRef.srcObject = remoteStream; audioRef.play().catch(() => {}); } }} autoPlay playsInline/>
            {callType === 'video' ? (
              <Box style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000', width: '100%', aspectRatio: '16/9', minHeight: 260 }}>
                {remoteStream ? (
                  <video ref={v => { if (v && remoteStream) v.srcObject = remoteStream; }} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }}/>
                ) : (
                  <Stack align="center" justify="center" style={{ position: 'absolute', inset: 0 }}>
                    <Avatar size={70} color={avatarColor(selectedChat?.clientName)} radius={70}>{getInitial(selectedChat?.clientName)}</Avatar>
                    <Text c="dimmed" size="sm">Connecting video…</Text>
                  </Stack>
                )}
                {localStream && (
                  <Box style={{ position: 'absolute', bottom: 12, right: 12, width: 130, aspectRatio: '16/9', borderRadius: 10, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.6)' }}>
                    <video ref={v => { if (v && localStream) v.srcObject = localStream; }} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}/>
                  </Box>
                )}
              </Box>
            ) : (
              <Box style={{
                width: 100, height: 100, borderRadius: '50%',
                background: 'linear-gradient(135deg, #00c853 0%, #00e676 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 0 12px rgba(0,200,83,0.15)'
              }}>
                <IconPhone size={48} color="white"/>
              </Box>
            )}
            <Text fw={700} c="#2d2d2d" size="lg">{selectedChat?.clientName || 'Customer'}</Text>
            <Group gap="sm" align="center">
              <Badge color="green" variant="dot" size="lg">● Connected</Badge>
              <Text fw={600} c="#2d2d2d" size="lg" style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.3)', padding: '4px 12px', borderRadius: '8px' }}>
                {formatCallDuration(callDuration)}
              </Text>
            </Group>
            <Group gap="lg" mt="sm">
              <Tooltip label="End call">
                <ActionIcon size={60} radius="xl" color="red" variant="filled" onClick={endCall}
                  style={{ boxShadow: '0 4px 20px rgba(255,0,0,0.4)' }}>
                  <IconPhoneOff size={28}/>
                </ActionIcon>
              </Tooltip>
            </Group>
          </Stack>
        </Modal>
      )}
    </Box>
  );
}
